/**
 * PdfViewerModal - PDF viewer with annotations and timber take-off
 */

import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { doc, getDoc } from 'firebase/firestore';
import { pdfjs } from 'react-pdf';
import { X, ChevronLeft, ChevronRight, MapPin, PanelLeft, Columns2, GitCompare, Link2, Search } from 'lucide-react';
import { getTakeoffPinsForDocument, createTakeoffPin, updateTakeoffPin, deleteTakeoffPin, pruneOrphanedStructurePins } from '../../utils/pdfTakeoffPinService';
import { subscribeToMarkupForDocument, createMarkupStroke, deleteMarkupStroke, updateMarkupStroke } from '../../utils/documentMarkupService';
import { canUseEngineeringMarkup, isSubcontractorRole } from '../../utils/userRoles';
import { ENG_STROKE, isEngineeringMarkupTool } from './engineeringMarkup';
import { useAuth } from '../../utils/AuthContext';
import { db } from '../../utils/firebase';
import SharedWholeProjectNote from '../pm/SharedWholeProjectNote';
import {
  publishDocumentCursor,
  clearDocumentCursor,
  cursorColorForUid,
  pointerToPageNorm,
} from '../../utils/documentPresenceService';
import { useOptionalCRM } from '../../utils/CRMContext';
import { useProjectHubScopedData } from '../../hooks/useProjectHubProjectData';
import { useCompareSyncHotkey } from '../../hooks/useCompareSyncHotkey';
import { usePreventViewerHistoryBack } from '../../hooks/usePreventViewerHistoryBack';
import { canAccessTakeoffFeatures } from '../../utils/salesRepAccess';
import type { PdfTakeoffPin, PdfTakeoffLink, DocumentMarkupStroke, DocumentMarkupTool, PdfMeasurement, PdfTakeoffStructure, PdfAnnotation } from '../../types';
import { AnnotationLayer } from './AnnotationLayer';
import { fileCommentNotifiesWithoutMention } from '../../utils/untaggedCommentConfirm';
import { TakeoffPinLayer } from './TakeoffPinLayer';
import { TakeoffLinkModal, type TakeoffLinkFormData } from './TakeoffLinkModal';
import { TakeoffSessionPanel } from './TakeoffSessionPanel';
import { StructureLabelsPanel } from './StructureLabelsPanel';
import { PdfCommentsPopover } from './PdfCommentsPopover';
import { ViewerToolbar, buildDownloadFilename } from './ViewerToolbar';
import { displayTitleForViewer } from '../../utils/projectFileRef';
import { canUseAsFirestoreDocId } from '../../utils/firestoreDocId';
import { PdfViewerErrorBoundary } from './PdfViewerErrorBoundary';
import { ViewerModeBar, type ViewerInteractionMode } from './ViewerModeBar';
import { useMarkupStrokeWidth } from './markupStrokeWidth';
import { MarkupLayer, type MarkupCommitPayload, type MarkupStrokePatch } from './MarkupLayer';
import DocumentPresenceLayer from './DocumentPresenceLayer';
import { MeasurementLayer, type MeasurementSubMode } from './MeasurementLayer';
import { MeasurementToolbar } from './MeasurementToolbar';
import CalibrationModal, { type CalibrationApplyPayload } from './CalibrationModal';
import { usePdfMeasurements } from '../../hooks/usePdfMeasurements';
import { detectPdfScaleFromDocument } from '../../utils/pdfScaleDetection';
import {
  addMeasurement,
  applyCalibrationToAllPages,
  clearPageMeasurements,
  deleteMeasurement,
  deletePageCalibration,
  saveCalibration,
  updateMeasurement,
} from '../../utils/pdfMeasurementService';
import {
  createTakeoffLink,
  subscribeDocumentTakeoffLinks,
  updateTakeoffLink,
} from '../../utils/pdfTakeoffLinkService';
import { subscribeDealTakeoffStructures } from '../../utils/pdfTakeoffStructureService';
import { computeMeasurementLengthFt, roundTimberLengthFt, measurementNormMidpoint, resolveInitialTakeoffStructureId, inferNearestTakeoffLinkForMeasurement, standardizeTimberDimensions } from '../../utils/timberTakeoffHelpers';
import type { ProposalTimberDefaults } from '../../utils/proposalTimberDefaults';
import { usePdfAnnotations } from '../../hooks/usePdfAnnotations';
import { assignedArchitectLabel } from '../../utils/commentAudience';
import { getAssignedArchitectUids } from '../../utils/architectProjectAccess';
import { isArchitectRole } from '../../utils/userRoles';
import type { CommentAudience } from '../../utils/commentAudience';
import { capturePdfPageContextPreview, capturePdfCommentPreviewFromDocument } from '../../utils/pdfCommentContextCapture';
import { markAnnotationAsRead } from '../../utils/pdfAnnotationService';
import {
  subscribeActivePlanReviewForDocument,
  startPlanReview,
  cancelPlanReview,
  completePlanReview,
  listAnnotationsForPlanReview,
  countMarkupStrokesForDocument,
  computePlanReviewDefaultRecipients,
} from '../../utils/planReviewService';
import { postPlanReviewDesignGroupComment } from '../../utils/planReviewDesignComment';
import type { PlanReview } from '../../types';
import PlanReviewCompleteModal from './PlanReviewCompleteModal';
import { canManagePlanReview as userCanManagePlanReview } from '../../utils/userRoles';
import type { DisplayUnit } from '../../utils/measurementMath';
import SetProjectAddressModal from './SetProjectAddressModal';
import { getProjectAddressForDeal, type ProjectAddressContext } from '../../utils/projectAddressService';
import PageThumbnailRail, { type PageCommentSummary } from './PageThumbnailRail';
import { capPdfRenderScale, shouldRenderPdfTextLayer } from '../../utils/pdfRenderScale';
import { pdfPageStepFromArrowKey } from '../../utils/pdfPageArrowKeys';
import { searchPdfDocument, type PdfTextSearchHit } from '../../utils/pdfTextSearch';
import { PdfViewerPane } from './PdfViewerPane';
import { PdfSearchBar } from './PdfSearchBar';
import { usePdfPaneView, type PdfJsLikeDoc } from './usePdfPaneView';
import {
  VIEWER_FIT_PADDING_PX,
  VIEWER_ZOOM_MAX,
  VIEWER_ZOOM_MIN,
  VIEWER_ZOOM_STEP,
  clampViewerZoom,
  computeViewerFitScale,
} from '../../utils/viewerZoom';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewerModal.css';

const THUMB_RAIL_PREF_KEY = 'pdfViewer:thumbRailVisible';

// Zoom uses an exponential / multiplicative model so each step changes the
// *visible area* by the same proportion regardless of starting zoom — the
// user perceives this as much more responsive than additive +/- 0.2 steps.
const ZOOM_WHEEL_SENSITIVITY = 0.005; // exponent factor for wheel deltaY


/** Group measurement midpoints into spatial clusters (e.g. front vs side view on one sheet). */
const STRUCTURE_VIEW_CLUSTER_RADIUS = 18;
/** Saved pin within this distance of a cluster centroid claims that view. */
const SAVED_PIN_CLUSTER_MATCH_RADIUS = 22;
/** Collapse duplicate saved pins stacked on the same spot. */
const SAVED_PIN_DEDUPE_RADIUS = 4;

function clusterNormPoints(
  points: { x: number; y: number }[],
  radius: number,
): { x: number; y: number }[] {
  const clusters: { x: number; y: number; count: number }[] = [];
  for (const point of points) {
    let assigned = false;
    for (const cluster of clusters) {
      if (Math.hypot(point.x - cluster.x, point.y - cluster.y) < radius) {
        const n = cluster.count + 1;
        cluster.x = (cluster.x * cluster.count + point.x) / n;
        cluster.y = (cluster.y * cluster.count + point.y) / n;
        cluster.count = n;
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      clusters.push({ x: point.x, y: point.y, count: 1 });
    }
  }
  return clusters.map(({ x, y }) => ({ x, y }));
}

function pinRank(pin: PdfTakeoffPin): number {
  return (pin.createdAt?.getTime() ?? 0) + (pin.id && !pin.id.startsWith('virtual-') ? 1e15 : 0);
}

/** Drop duplicate saved pins for the same structure at nearly the same coordinates. */
function dedupeSavedPinsOnPage(pins: PdfTakeoffPin[]): PdfTakeoffPin[] {
  const kept: PdfTakeoffPin[] = [];
  for (const pin of pins) {
    const duplicateIdx = kept.findIndex(
      existing =>
        existing.structureId === pin.structureId &&
        Math.hypot(existing.x - pin.x, existing.y - pin.y) < SAVED_PIN_DEDUPE_RADIUS,
    );
    if (duplicateIdx === -1) {
      kept.push(pin);
      continue;
    }
    if (pinRank(pin) >= pinRank(kept[duplicateIdx])) {
      kept[duplicateIdx] = pin;
    }
  }
  return kept;
}

function nearestClusterIndex(
  x: number,
  y: number,
  clusters: { x: number; y: number }[],
  maxDistance: number,
): number | null {
  let bestIdx: number | null = null;
  let bestDist = maxDistance;
  clusters.forEach((cluster, index) => {
    const dist = Math.hypot(x - cluster.x, y - cluster.y);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = index;
    }
  });
  return bestIdx;
}

interface PdfScrollSnap {
  wrapLeft: number;
  wrapTop: number;
  mainLeft: number;
  mainTop: number;
}

function capturePdfScrollSnap(wrap: HTMLDivElement | null): PdfScrollSnap | null {
  if (!wrap) return null;
  const main = wrap.closest('.pdf-viewer-main') as HTMLElement | null;
  return {
    wrapLeft: wrap.scrollLeft,
    wrapTop: wrap.scrollTop,
    mainLeft: main?.scrollLeft ?? 0,
    mainTop: main?.scrollTop ?? 0,
  };
}

function restorePdfScrollSnap(wrap: HTMLDivElement | null, snap: PdfScrollSnap) {
  if (!wrap) return;
  const main = wrap.closest('.pdf-viewer-main') as HTMLElement | null;
  wrap.scrollLeft = snap.wrapLeft;
  wrap.scrollTop = snap.wrapTop;
  if (main) {
    main.scrollLeft = snap.mainLeft;
    main.scrollTop = snap.mainTop;
  }
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export interface PdfViewerSiblingVersion {
  documentId: string;
  url: string;
  title: string;
  versionLabel?: string;
}

export interface PdfViewerModalProps {
  url: string;
  title: string;
  documentId: string;
  leadId: string;
  leadName?: string;
  projectId?: string;
  dealId?: string;
  /** Version stream id when opened from Project Files. */
  streamId?: string;
  /** Other PDFs in the same Files stream (Compare / version split). */
  siblingVersions?: PdfViewerSiblingVersion[];
  /** Open already split against this sibling `documentId`. */
  initialCompareDocumentId?: string;
  /** OPS / PM task id for annotation provenance. */
  taskId?: string;
  /** Hide timber take-off UI (e.g. OPS task attachments). */
  disableTakeoff?: boolean;
  /** Seed pins when Firestore is not readable (public trade briefing). */
  initialAnnotations?: PdfAnnotation[];
  initialAnnotationId?: string;
  initialPageNumber?: number;
  /** Architect portal / hub: decision pins only. */
  architectSurface?: boolean;
  /** This document is the one whole-project file, opened from any structure track. */
  sharedAcrossStructures?: boolean;
  onClose: () => void;
}

function PdfViewerModalInner({
  url,
  title: titleProp,
  documentId,
  leadId,
  leadName,
  projectId: projectIdProp,
  dealId,
  streamId,
  siblingVersions = [],
  initialCompareDocumentId,
  taskId,
  disableTakeoff = false,
  initialAnnotations,
  initialAnnotationId,
  initialPageNumber,
  architectSurface = false,
  sharedAcrossStructures = false,
  onClose,
}: PdfViewerModalProps) {
  // Must not throw on nullish titles — a render crash blanks the CRM shell (#39).
  const title = displayTitleForViewer(titleProp, url);
  const { user } = useAuth();
  usePreventViewerHistoryBack(true);
  const [fileOwnerProjectId, setFileOwnerProjectId] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setFileOwnerProjectId(null);
    // Comment attachments / purchase paths may pass a download URL or storage
    // path as documentId. `doc()` throws synchronously on `/` (and `//` from
    // https://) — that must never reach path construction.
    if (!canUseAsFirestoreDocId(documentId)) return;
    try {
      void getDoc(doc(db, 'projectFiles', documentId))
        .then((snap) => {
          if (cancelled || !snap.exists()) return;
          const pid = String(snap.data()?.projectId || '').trim();
          if (pid) setFileOwnerProjectId(pid);
        })
        .catch(() => {});
    } catch {
      /* invalid path segment — ignore */
    }
    return () => {
      cancelled = true;
    };
  }, [documentId]);
  // Pins, markup, and the review card follow the file's project, not whichever
  // project happens to be selected in the hub while this viewer stays open.
  const projectId = fileOwnerProjectId || projectIdProp;
  const isInvitedTrade = isSubcontractorRole(user?.role);
  const crm = useOptionalCRM();
  const crmUsers = crm?.crmUsers ?? [];
  const timberProjects = crm?.timberProjects ?? [];
  const enhancedDeals = crm?.enhancedDeals ?? [];
  const crmTaskGroups = crm?.taskGroups ?? [];
  const crmProjectTasks = crm?.projectTasks ?? [];
  const crmProjectOnboardings = crm?.projectOnboardings ?? [];
  // Inside the Project Hub the selected project's scoped data is available;
  // outside (lead docs etc.) the org-wide arrays are used as before.
  const scopedHubData = useProjectHubScopedData();
  const taskGroups = scopedHubData?.taskGroups ?? crmTaskGroups;
  const projectTasks = scopedHubData?.tasks ?? crmProjectTasks;
  const projectOnboardings = scopedHubData?.projectOnboardings ?? crmProjectOnboardings;
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const spaceScrollLockRef = useRef<PdfScrollSnap | null>(null);
  const pendingScrollRestoreRef = useRef<PdfScrollSnap | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(initialPageNumber ?? 1);
  const [pageWidth, setPageWidth] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [fitMode, setFitMode] = useState(true);
  const fileUrl = (url || '').trim();
  const [loading, setLoading] = useState(() => !!fileUrl);
  const [error, setError] = useState<string | null>(() =>
    fileUrl ? null : 'No file URL was provided for this document.',
  );
  const [takeoffPins, setTakeoffPins] = useState<PdfTakeoffPin[]>([]);
  const [takeoffLinks, setTakeoffLinks] = useState<PdfTakeoffLink[]>([]);
  const [takeoffDraftStructures, setTakeoffDraftStructures] = useState<PdfTakeoffStructure[]>([]);
  const [takeoffModeActive, setTakeoffModeActive] = useState(false);
  const [pinMode, setPinMode] = useState<{
    structureId: string;
    structureName: string;
    organizational?: boolean;
  } | null>(null);
  const [structureLabelsOpen, setStructureLabelsOpen] = useState(false);
  const [selectedStructureFromPin, setSelectedStructureFromPin] = useState<string | null>(null);
  const [activeTakeoffStructureId, setActiveTakeoffStructureId] = useState<string | null>(null);
  const [linkModalMeasurement, setLinkModalMeasurement] = useState<PdfMeasurement | null>(null);
  const [editingLink, setEditingLink] = useState<PdfTakeoffLink | null>(null);
  const [lastTimberDefaults, setLastTimberDefaults] = useState<ProposalTimberDefaults | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [renderedPageNumber, setRenderedPageNumber] = useState<number | null>(null);
  const [baseScale, setBaseScale] = useState(1);
  const [pageNaturalSize, setPageNaturalSize] = useState({ w: 612, h: 792 });
  const baseScaleRef = useRef(1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const initialScaleSetRef = useRef(false);
  const pdfDocForScaleRef = useRef<PdfJsLikeDoc | null>(null);
  const scaleDetectAttemptedRef = useRef(false);
  /** Tracks which deep-link target we already navigated to so in-viewer comment hops don't snap back. */
  const initialNavAppliedRef = useRef<string | null>(null);
  const [scaleDetectionBanner, setScaleDetectionBanner] = useState<string | null>(null);

  const [markupStrokes, setMarkupStrokes] = useState<DocumentMarkupStroke[]>([]);
  const [viewerMode, setViewerMode] = useState<ViewerInteractionMode>('comment');
  const [drawTool, setDrawTool] = useState<DocumentMarkupTool>('pen');
  const [drawColor, setDrawColor] = useState('#3b82f6');
  const { strokeWidth: drawStrokeWidth, setStrokeWidth: setDrawStrokeWidth } = useMarkupStrokeWidth(drawTool);
  const [selectedStampId, setSelectedStampId] = useState('peg');
  const canEngineerMarkup = canUseEngineeringMarkup(user?.role);

  type SplitKind = 'same' | 'version';
  const [splitKind, setSplitKind] = useState<SplitKind | null>(null);
  const [focusedPane, setFocusedPane] = useState<'left' | 'right'>('left');
  const [syncViews, setSyncViews] = useState(false);
  const [rightSiblingId, setRightSiblingId] = useState<string | null>(null);
  const [splitRatio, setSplitRatio] = useState(0.5);
  const [splitAllowed, setSplitAllowed] = useState(() =>
    typeof window === 'undefined' ? true : window.innerWidth >= 900,
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHits, setSearchHits] = useState<PdfTextSearchHit[]>([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const splitStageRef = useRef<HTMLDivElement>(null);
  const splitDraggingRef = useRef(false);
  const syncingScrollRef = useRef(false);
  const compareOpenedRef = useRef(false);

  const pdfSiblings = useMemo(
    () => siblingVersions.filter(v => v.documentId && v.documentId !== documentId),
    [siblingVersions, documentId],
  );
  const previousSibling = useMemo(() => {
    const idx = siblingVersions.findIndex(v => v.documentId === documentId);
    if (idx >= 0) {
      return siblingVersions.slice(idx + 1).find(v => v.documentId !== documentId)
        || siblingVersions.slice(0, idx).reverse().find(v => v.documentId !== documentId)
        || null;
    }
    return pdfSiblings[0] || null;
  }, [siblingVersions, documentId, pdfSiblings]);
  const rightSibling =
    pdfSiblings.find(v => v.documentId === rightSiblingId) || previousSibling;
  const openedVersionLabel =
    siblingVersions.find(v => v.documentId === documentId)?.versionLabel || title;
  const splitOpen = splitKind !== null && splitAllowed;
  useCompareSyncHotkey(splitOpen, () => setSyncViews(v => !v));
  const versionSplit = splitOpen && splitKind === 'version' && !!rightSibling;
  const focusedIsRight = splitOpen && focusedPane === 'right';
  const rightFileUrl = splitOpen ? (versionSplit && rightSibling ? rightSibling.url : fileUrl) : null;
  const rightView = usePdfPaneView(rightFileUrl, pageNumber);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 900px)');
    const apply = () => setSplitAllowed(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!splitAllowed && splitKind) {
      setSplitKind(null);
      setFocusedPane('left');
    }
  }, [splitAllowed, splitKind]);

  useEffect(() => {
    if (compareOpenedRef.current || !initialCompareDocumentId || !splitAllowed) return;
    const match = siblingVersions.find(v => v.documentId === initialCompareDocumentId);
    if (!match) return;
    compareOpenedRef.current = true;
    setRightSiblingId(match.documentId);
    setSplitKind('version');
    setSyncViews(true);
    setFocusedPane('left');
  }, [initialCompareDocumentId, siblingVersions, splitAllowed]);

  // ----- Measurement state -----
  const { calibration, measurements, allMeasurements, allCalibrations } = usePdfMeasurements(documentId, pageNumber);
  const rightDocMeasurements = usePdfMeasurements(
    versionSplit && rightSibling ? rightSibling.documentId : null,
    rightView.pageNumber,
  );
  const samePdfRightCalibration = useMemo(() => {
    const matches = allCalibrations.filter(c => c.pageNumber === rightView.pageNumber);
    if (matches.length === 0) return null;
    return matches.slice().sort((a, b) =>
      (b.updatedAt?.getTime() || b.createdAt.getTime()) - (a.updatedAt?.getTime() || a.createdAt.getTime()),
    )[0];
  }, [allCalibrations, rightView.pageNumber]);
  const rightPaneCalibration = versionSplit ? rightDocMeasurements.calibration : samePdfRightCalibration;
  const rightPaneMeasurements = versionSplit
    ? rightDocMeasurements.measurements
    : allMeasurements.filter(m => m.pageNumber === rightView.pageNumber);
  const [measureSubMode, setMeasureSubMode] = useState<MeasurementSubMode>('linear');
  const [calibrationModalOpen, setCalibrationModalOpen] = useState(false);
  const [twoPointCapture, setTwoPointCapture] = useState<'inactive' | 'awaitingFirst' | 'awaitingSecond'>('inactive');
  const [twoPointA, setTwoPointA] = useState<{ x: number; y: number } | null>(null);
  const [twoPointB, setTwoPointB] = useState<{ x: number; y: number } | null>(null);
  const [displayUnitOverride, setDisplayUnitOverride] = useState<DisplayUnit | null>(null);
  const [lockedToast, setLockedToast] = useState<string | null>(null);
  const [selectionResetToken, setSelectionResetToken] = useState(0);
  // Hold-to-toggle: P key flips angle/linear labels into roof-pitch mode.
  const [pitchMode, setPitchMode] = useState(false);
  // Hold Space momentarily to reveal all take-off dimension details and click labels to edit.
  const [spaceHeld, setSpaceHeld] = useState(false);

  useEffect(() => {
    if (viewerMode !== 'measure' && viewerMode !== 'takeoff') {
      setSpaceHeld(false);
      return;
    }

    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.key === 'Escape') {
        if (!linkModalMeasurement) {
          setSpaceHeld(false);
          spaceScrollLockRef.current = null;
        }
        return;
      }
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Always suppress Space default (page scroll / focused button activation).
      e.preventDefault();
      e.stopImmediatePropagation();
      if (linkModalMeasurement) return;
      if (!e.repeat) {
        setSpaceHeld(true);
        spaceScrollLockRef.current = capturePdfScrollSnap(canvasWrapRef.current);
      }
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const snap = pendingScrollRestoreRef.current ?? spaceScrollLockRef.current;
      if (snap) {
        restorePdfScrollSnap(canvasWrapRef.current, snap);
      }
      if (linkModalMeasurement) return;
      setSpaceHeld(false);
      spaceScrollLockRef.current = null;
    };
    const onBlur = () => {
      if (linkModalMeasurement) return;
      setSpaceHeld(false);
      spaceScrollLockRef.current = null;
    };
    window.addEventListener('keydown', onDown, { capture: true });
    window.addEventListener('keyup', onUp, { capture: true });
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown, { capture: true });
      window.removeEventListener('keyup', onUp, { capture: true });
      window.removeEventListener('blur', onBlur);
      if (!linkModalMeasurement) {
        setSpaceHeld(false);
      }
    };
  }, [viewerMode, linkModalMeasurement]);

  useEffect(() => {
    const shouldLockScroll = spaceHeld || !!linkModalMeasurement;
    if (!shouldLockScroll) return;
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const main = wrap.closest('.pdf-viewer-main') as HTMLElement | null;
    const snap =
      pendingScrollRestoreRef.current
      ?? spaceScrollLockRef.current
      ?? capturePdfScrollSnap(wrap);
    if (!snap) return;
    spaceScrollLockRef.current = snap;

    const lockScroll = () => {
      restorePdfScrollSnap(wrap, snap);
    };

    wrap.addEventListener('scroll', lockScroll, { passive: true });
    main?.addEventListener('scroll', lockScroll, { passive: true });
    return () => {
      wrap.removeEventListener('scroll', lockScroll);
      main?.removeEventListener('scroll', lockScroll);
    };
  }, [spaceHeld, linkModalMeasurement]);

  useLayoutEffect(() => {
    if (!linkModalMeasurement) return;
    const snap = pendingScrollRestoreRef.current ?? spaceScrollLockRef.current;
    if (!snap) return;
    const wrap = canvasWrapRef.current;
    const restore = () => restorePdfScrollSnap(wrap, snap);
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
  }, [linkModalMeasurement]);

  useEffect(() => {
    if (linkModalMeasurement) return;
    pendingScrollRestoreRef.current = null;
    if (!spaceHeld) {
      spaceScrollLockRef.current = null;
    }
  }, [linkModalMeasurement, spaceHeld]);

  useEffect(() => {
    if (!pinMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      setPinMode(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [pinMode]);

  useEffect(() => {
    if (viewerMode !== 'measure' && viewerMode !== 'takeoff') {
      setPitchMode(false);
      return;
    }
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'p' || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      setPitchMode(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'p') setPitchMode(false);
    };
    const onBlur = () => setPitchMode(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      setPitchMode(false);
    };
  }, [viewerMode]);

  useEffect(() => {
    if (!lockedToast) return;
    const id = setTimeout(() => setLockedToast(null), 3000);
    return () => clearTimeout(id);
  }, [lockedToast]);

  // Reset display-unit override when calibration changes (different unit system).
  useEffect(() => {
    setDisplayUnitOverride(null);
  }, [calibration?.unitSystem, pageNumber]);

  // ----- Project address quick-set state -----
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressCtx, setAddressCtx] = useState<ProjectAddressContext | null>(null);
  const [addressToast, setAddressToast] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId || isInvitedTrade) {
      setAddressCtx(null);
      return;
    }
    let cancelled = false;
    void getProjectAddressForDeal(dealId).then(ctx => {
      if (!cancelled) setAddressCtx(ctx);
    }).catch(err => {
      console.error('Failed to load project address context:', err);
    });
    return () => { cancelled = true; };
  }, [dealId, isInvitedTrade]);

  useEffect(() => {
    if (!addressToast) return;
    const id = setTimeout(() => setAddressToast(null), 2800);
    return () => clearTimeout(id);
  }, [addressToast]);

  // ----- Page thumbnail rail -----
  const [thumbRailVisible, setThumbRailVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(THUMB_RAIL_PREF_KEY);
    return stored === null ? true : stored === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(THUMB_RAIL_PREF_KEY, thumbRailVisible ? '1' : '0');
  }, [thumbRailVisible]);

  const captureContextPreview = useCallback(
    async (pageNumber: number, x: number, y: number) => {
      const marker = user
        ? {
            userName: user.name || user.email || 'Unknown',
            userId: user.id,
            photoURL: user.photoURL,
          }
        : undefined;
      const wrap = canvasWrapRef.current;
      if (!wrap) return undefined;
      const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
      const domCapture = pageContainer
        ? await capturePdfPageContextPreview(pageContainer, x, y, marker)
        : undefined;
      if (domCapture) return domCapture;
      return capturePdfCommentPreviewFromDocument(url, pageNumber, x, y, marker);
    },
    [url, user],
  );

  const [activePlanReview, setActivePlanReview] = useState<PlanReview | null>(null);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [completeDefaults, setCompleteDefaults] = useState<string[]>([]);
  const [completeReviewAnnotations, setCompleteReviewAnnotations] = useState<PdfAnnotation[]>([]);
  const [planReviewBusy, setPlanReviewBusy] = useState(false);
  const canManagePlanReview = userCanManagePlanReview(user?.role);

  useEffect(() => {
    if (isInvitedTrade) {
      setActivePlanReview(null);
      return;
    }
    return subscribeActivePlanReviewForDocument(documentId, setActivePlanReview);
  }, [documentId, isInvitedTrade]);

  const viewerDeal = useMemo(
    () => (dealId ? enhancedDeals.find((d) => d.id === dealId) || null : null),
    [dealId, enhancedDeals],
  );
  const viewerProject = useMemo(
    () => (projectId ? timberProjects.find((p) => p.id === projectId) || null : null),
    [projectId, timberProjects],
  );
  const pinArchitectName = useMemo(
    () => assignedArchitectLabel(crmUsers, viewerDeal, viewerProject),
    [crmUsers, viewerDeal, viewerProject],
  );
  const hasAssignedArchitect = getAssignedArchitectUids(viewerDeal, viewerProject).length > 0;
  const [pinAudience, setPinAudience] = useState<CommentAudience>('internal');
  const effectivePinAudience: CommentAudience =
    architectSurface || isArchitectRole(user?.role) ? 'architect' : pinAudience;
  const {
    annotations,
    loadingAnnotations,
    focusedAnnotationId,
    setFocusedAnnotationId,
    loadAnnotations,
    handleAddAnnotation,
    handleAddReply,
  } = usePdfAnnotations({
    documentId,
    documentUrl: url,
    leadId,
    projectId,
    taskId,
    user,
    initialAnnotations,
    initialAnnotationId,
    captureContextPreview,
    activePlanReviewId: activePlanReview?.id,
    decisionOnly: architectSurface,
    pinAudience: effectivePinAudience,
  });

  const captureRightContextPreview = useCallback(
    async (pn: number, x: number, y: number) => {
      const marker = user
        ? {
            userName: user.name || user.email || 'Unknown',
            userId: user.id,
            photoURL: user.photoURL,
          }
        : undefined;
      const wrap = rightView.canvasWrapRef.current;
      if (!wrap || !rightSibling) return undefined;
      const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
      const domCapture = pageContainer
        ? await capturePdfPageContextPreview(pageContainer, x, y, marker)
        : undefined;
      if (domCapture) return domCapture;
      return capturePdfCommentPreviewFromDocument(rightSibling.url, pn, x, y, marker);
    },
    [rightView.canvasWrapRef, rightSibling, user],
  );

  const {
    annotations: rightAnnotations,
    loadingAnnotations: rightLoadingAnnotations,
    focusedAnnotationId: rightFocusedAnnotationId,
    setFocusedAnnotationId: setRightFocusedAnnotationId,
    loadAnnotations: loadRightAnnotations,
    handleAddAnnotation: handleAddRightAnnotation,
    handleAddReply: handleAddRightReply,
  } = usePdfAnnotations({
    documentId: versionSplit && rightSibling ? rightSibling.documentId : '',
    documentUrl: versionSplit && rightSibling ? rightSibling.url : '',
    leadId,
    projectId,
    taskId,
    user,
    captureContextPreview: captureRightContextPreview,
    decisionOnly: architectSurface,
    pinAudience: effectivePinAudience,
  });

  const loadTakeoffPins = useCallback(async () => {
    if (isInvitedTrade) {
      setTakeoffPins([]);
      return;
    }
    try {
      const list = await getTakeoffPinsForDocument(documentId, url);
      setTakeoffPins(list);
    } catch (err) {
      console.error('Error loading take-off pins:', err);
    }
  }, [documentId, url, isInvitedTrade]);

  useEffect(() => {
    loadTakeoffPins();
  }, [loadTakeoffPins]);

  useEffect(() => {
    if (isInvitedTrade) {
      setTakeoffLinks([]);
      return;
    }
    const unsub = subscribeDocumentTakeoffLinks(documentId, setTakeoffLinks);
    return () => unsub();
  }, [documentId, isInvitedTrade]);

  /** Remove take-off pins on a page when that structure no longer has linked items there. */
  useEffect(() => {
    if (!dealId || isInvitedTrade) return;
    let cancelled = false;
    void (async () => {
      try {
        const pins = await getTakeoffPinsForDocument(documentId, url);
        const removed = await pruneOrphanedStructurePins(pins, takeoffLinks);
        if (removed > 0 && !cancelled) await loadTakeoffPins();
      } catch (err) {
        console.error('Error pruning orphaned structure pins:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, isInvitedTrade, takeoffLinks, documentId, url, loadTakeoffPins]);

  useEffect(() => {
    if (!dealId || isInvitedTrade) {
      setTakeoffDraftStructures([]);
      return;
    }
    const unsub = subscribeDealTakeoffStructures(dealId, setTakeoffDraftStructures);
    return () => unsub();
  }, [dealId, isInvitedTrade]);

  const canAccessTakeoff = useMemo(() => {
    if (disableTakeoff) return false;
    if (!user?.id) return false;
    const row = crmUsers.find(u => u.uid === user.id);
    const designations = row?.designations ?? user.designations;
    return canAccessTakeoffFeatures({ authRole: user.role, designations });
  }, [disableTakeoff, user, crmUsers]);

  const linkedMeasurementIds = useMemo(
    () => new Set(takeoffLinks.map(l => l.measurementId)),
    [takeoffLinks],
  );

  const linkByMeasurementId = useMemo(() => {
    const map = new Map<string, PdfTakeoffLink>();
    for (const link of takeoffLinks) {
      map.set(link.measurementId, link);
    }
    return map;
  }, [takeoffLinks]);

  const pagesWithTakeoffActivity = useMemo(() => {
    const pages = new Set<number>();
    for (const link of takeoffLinks) pages.add(link.pageNumber);
    for (const pin of takeoffPins) pages.add(pin.pageNumber);
    return pages;
  }, [takeoffLinks, takeoffPins]);

  const currentPageHasTakeoffActivity = pagesWithTakeoffActivity.has(pageNumber);

  /** Hide take-off measurements outside take-off mode; keep generic measure lines on other pages. */
  const displayMeasurements = useMemo(() => {
    if (takeoffModeActive) return measurements;
    if (pagesWithTakeoffActivity.has(pageNumber)) return [];
    return measurements;
  }, [measurements, takeoffModeActive, pageNumber, pagesWithTakeoffActivity]);

  /** Saved pins plus virtual badges per measurement cluster (supports multiple views per structure). */
  const structureIndicatorsOnPage = useMemo((): PdfTakeoffPin[] => {
    const savedOnPage = dedupeSavedPinsOnPage(
      takeoffPins.filter(p => p.pageNumber === pageNumber),
    );
    if (!dealId) return savedOnPage;

    const linksOnPage = takeoffLinks.filter(l => l.pageNumber === pageNumber);
    const byStructure = new Map<string, PdfTakeoffLink[]>();
    for (const link of linksOnPage) {
      const list = byStructure.get(link.structureId) ?? [];
      list.push(link);
      byStructure.set(link.structureId, list);
    }

    const virtual: PdfTakeoffPin[] = [];
    Array.from(byStructure.entries()).forEach(([structureId, links]) => {
      const structureName = links[0]?.structureName ?? '';
      const midpoints: { x: number; y: number }[] = [];
      for (const link of links) {
        const m = allMeasurements.find(me => me.id === link.measurementId);
        if (!m) continue;
        const mid = measurementNormMidpoint(m);
        if (mid) midpoints.push(mid);
      }
      if (midpoints.length === 0) return;

      const clusters = clusterNormPoints(midpoints, STRUCTURE_VIEW_CLUSTER_RADIUS);
      const structureSavedPins = savedOnPage.filter(p => p.structureId === structureId);
      const claimedClusters = new Set<number>();
      for (const pin of structureSavedPins) {
        const clusterIdx = nearestClusterIndex(
          pin.x,
          pin.y,
          clusters,
          SAVED_PIN_CLUSTER_MATCH_RADIUS,
        );
        if (clusterIdx !== null) claimedClusters.add(clusterIdx);
      }

      clusters.forEach((centroid, clusterIndex) => {
        if (claimedClusters.has(clusterIndex)) return;
        virtual.push({
          id: `virtual-${structureId}-${pageNumber}-${clusterIndex}`,
          documentId,
          documentUrl: url,
          leadId,
          dealId,
          structureId,
          structureName,
          pageNumber,
          x: centroid.x,
          y: centroid.y,
          createdAt: new Date(),
        });
      });
    });

    return [...savedOnPage, ...virtual];
  }, [
    dealId,
    takeoffPins,
    takeoffLinks,
    allMeasurements,
    pageNumber,
    documentId,
    url,
    leadId,
  ]);

  const showStructureIndicators = takeoffModeActive || !!pinMode || currentPageHasTakeoffActivity;

  useEffect(() => {
    if (isInvitedTrade) {
      setMarkupStrokes([]);
      return;
    }
    const unsub = subscribeToMarkupForDocument(documentId, url, setMarkupStrokes);
    return () => unsub();
  }, [documentId, url, isInvitedTrade]);

  const [rightMarkupStrokes, setRightMarkupStrokes] = useState<DocumentMarkupStroke[]>([]);
  useEffect(() => {
    if (!versionSplit || !rightSibling || isInvitedTrade) {
      setRightMarkupStrokes([]);
      return;
    }
    const unsub = subscribeToMarkupForDocument(rightSibling.documentId, rightSibling.url, setRightMarkupStrokes);
    return () => unsub();
  }, [versionSplit, rightSibling, isInvitedTrade]);

  const commitMarkupFor = useCallback(
    (target: { documentId: string; documentUrl: string }) =>
      async (payload: MarkupCommitPayload) => {
        if (!user?.id) return;
        try {
          return await createMarkupStroke({
            documentId: target.documentId,
            documentUrl: target.documentUrl,
            leadId,
            ...(projectId ? { projectId } : {}),
            pageNumber: payload.pageNumber,
            tool: payload.tool,
            color: payload.color,
            strokeWidth: payload.strokeWidth,
            points: payload.points,
            userId: user.id,
            userName: user.name || user.email || 'Unknown',
            createdAt: new Date(),
            ...(payload.text != null ? { text: payload.text } : {}),
            ...(payload.fill ? { fill: payload.fill } : {}),
            ...(payload.textColor ? { textColor: payload.textColor } : {}),
            ...(payload.stampId ? { stampId: payload.stampId } : {}),
            ...(typeof payload.stampScale === 'number' ? { stampScale: payload.stampScale } : {}),
            ...(typeof payload.stampRotation === 'number' ? { stampRotation: payload.stampRotation } : {}),
          });
        } catch (err) {
          console.error('Error saving markup:', err);
        }
      },
    [leadId, projectId, user],
  );

  const onMarkupCommit = useCallback(
    (payload: MarkupCommitPayload) => commitMarkupFor({ documentId, documentUrl: url })(payload),
    [commitMarkupFor, documentId, url],
  );

  const onRightMarkupCommit = useCallback(
    async (payload: MarkupCommitPayload) => {
      if (!rightSibling) return;
      return commitMarkupFor({ documentId: rightSibling.documentId, documentUrl: rightSibling.url })(payload);
    },
    [commitMarkupFor, rightSibling],
  );

  const onMarkupUpdate = useCallback(async (strokeId: string, patch: MarkupStrokePatch) => {
    try {
      await updateMarkupStroke(strokeId, patch);
    } catch (err) {
      console.error('Error updating markup:', err);
    }
  }, []);

  const handleViewerModeChange = useCallback((next: ViewerInteractionMode) => {
    setViewerMode(next);
    if (next === 'engineer') {
      if (!isEngineeringMarkupTool(drawTool) && drawTool !== 'eraser') setDrawTool('callout');
      if (drawColor === '#3b82f6') setDrawColor(ENG_STROKE);
    } else if (next === 'draw') {
      if (isEngineeringMarkupTool(drawTool)) setDrawTool('pen');
      if (drawColor === ENG_STROKE) setDrawColor('#3b82f6');
    }
  }, [drawTool, drawColor]);

  const onMarkupDelete = useCallback(async (strokeId: string) => {
    try {
      await deleteMarkupStroke(strokeId);
    } catch (err) {
      console.error('Error deleting markup:', err);
    }
  }, []);

  /* ------------- Measurement handlers ------------- */

  const onRightMeasurementCommit = useCallback(
    async (payload: { pageNumber: number; kind: 'linear' | 'polyline' | 'area' | 'angle'; points: { x: number; y: number }[] }) => {
      if (!user?.id || !rightSibling) return;
      try {
        await addMeasurement({
          documentId: rightSibling.documentId,
          documentUrl: rightSibling.url,
          pageNumber: payload.pageNumber,
          kind: payload.kind,
          points: payload.points,
          ...(leadId ? { leadId } : {}),
          ...(projectId ? { projectId } : {}),
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
        });
      } catch (err) {
        console.error('Error saving measurement:', err);
      }
    },
    [rightSibling, leadId, projectId, user],
  );

  const onMeasurementCommit = useCallback(
    async (payload: { pageNumber: number; kind: 'linear' | 'polyline' | 'area' | 'angle'; points: { x: number; y: number }[] }) => {
      if (!user?.id) return;
      try {
        const measurementId = await addMeasurement({
          documentId,
          documentUrl: url,
          pageNumber: payload.pageNumber,
          kind: payload.kind,
          points: payload.points,
          ...(leadId ? { leadId } : {}),
          ...(projectId ? { projectId } : {}),
          userId: user.id,
          userName: user.name || user.email || 'Unknown',
        });
        if (takeoffModeActive && dealId) {
          const measurement: PdfMeasurement = {
            id: measurementId,
            documentId,
            documentUrl: url,
            pageNumber: payload.pageNumber,
            kind: payload.kind,
            points: payload.points,
            userId: user.id,
            userName: user.name || user.email || 'Unknown',
            createdAt: new Date(),
          };
          setEditingLink(null);
          setLinkModalMeasurement(measurement);
        }
      } catch (err) {
        console.error('Error saving measurement:', err);
      }
    },
    [documentId, url, leadId, projectId, user, takeoffModeActive, dealId],
  );

  const onMeasurementUpdate = useCallback(
    async (id: string, points: { x: number; y: number }[]) => {
      try {
        await updateMeasurement(id, { points });
        const link = takeoffLinks.find(l => l.measurementId === id);
        if (!link?.id) return;
        const measurement = allMeasurements.find(m => m.id === id);
        if (!measurement) return;
        const ft = computeMeasurementLengthFt(
          { ...measurement, points },
          calibration,
          pageNaturalSize.w,
          pageNaturalSize.h,
        );
        if (ft == null || Number.isNaN(ft)) return;
        await updateTakeoffLink(link.id, {
          measuredLength: roundTimberLengthFt(ft),
          measuredLengthUnit: 'ft',
        });
      } catch (err) {
        console.error('Error updating measurement:', err);
      }
    },
    [takeoffLinks, allMeasurements, calibration, pageNaturalSize.w, pageNaturalSize.h],
  );

  const onMeasurementDelete = useCallback(async (id: string) => {
    try {
      await deleteMeasurement(id);
    } catch (err) {
      console.error('Error deleting measurement:', err);
    }
  }, []);

  const onClearPageMeasurements = useCallback(async () => {
    const ok = window.confirm('Delete every measurement on this page?');
    if (!ok) return;
    try {
      await clearPageMeasurements(documentId, pageNumber);
      setSelectionResetToken(t => t + 1);
    } catch (err) {
      console.error('Error clearing measurements:', err);
    }
  }, [documentId, pageNumber]);

  const onClearCalibration = useCallback(async () => {
    if (!calibration?.id) return;
    const ok = window.confirm('Remove the scale on this page? Existing measurement labels will read "(no scale)" until a new scale is set.');
    if (!ok) return;
    try {
      await deletePageCalibration(calibration.id);
    } catch (err) {
      console.error('Error clearing calibration:', err);
    }
  }, [calibration?.id]);

  const onApplyCalibration = useCallback(
    async (payload: CalibrationApplyPayload) => {
      const base = {
        documentId,
        documentUrl: url,
        mode: payload.mode,
        standardScaleId: payload.standardScaleId,
        pointA: payload.pointA,
        pointB: payload.pointB,
        realDistance: payload.realDistance,
        realDistanceUnit: payload.realDistanceUnit,
        realPerPdfPoint: payload.realPerPdfPoint,
        unitSystem: payload.unitSystem,
        baseUnit: payload.baseUnit,
        displayUnit: payload.displayUnit,
        pageNaturalWidth: pageNaturalSize.w,
        pageNaturalHeight: pageNaturalSize.h,
        ...(leadId ? { leadId } : {}),
        ...(projectId ? { projectId } : {}),
        ...(user?.id ? { userId: user.id } : {}),
        ...(user ? { userName: user.name || user.email || 'Unknown' } : {}),
      };
      if (payload.applyToAllPages && numPages > 1) {
        await applyCalibrationToAllPages(documentId, numPages, base);
      } else {
        await saveCalibration({ ...base, pageNumber });
      }
      // Reset two-point capture state once the calibration is saved.
      setTwoPointA(null);
      setTwoPointB(null);
      setTwoPointCapture('inactive');
    },
    [documentId, url, pageNaturalSize.w, pageNaturalSize.h, leadId, projectId, user, numPages, pageNumber],
  );

  const handleRequestTwoPointCapture = useCallback(() => {
    setCalibrationModalOpen(false);
    setTwoPointA(null);
    setTwoPointB(null);
    setTwoPointCapture('awaitingFirst');
  }, []);

  const handleCaptureClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      const p = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
      if (twoPointCapture === 'awaitingFirst') {
        setTwoPointA(p);
        setTwoPointCapture('awaitingSecond');
      } else if (twoPointCapture === 'awaitingSecond') {
        setTwoPointB(p);
        setTwoPointCapture('inactive');
        // Re-open the modal once both points are captured.
        setCalibrationModalOpen(true);
      }
    },
    [twoPointCapture],
  );

  useEffect(() => {
    setRenderedPageNumber(null);
    setBaseScale(1);
    baseScaleRef.current = 1;
    initialScaleSetRef.current = false;
  }, [url]);

  const handleSelectComment = useCallback(
    (annotationId: string, page: number) => {
      if (focusedIsRight) {
        rightView.setPageNumber(page);
        if (versionSplit) setRightFocusedAnnotationId(annotationId);
        else setFocusedAnnotationId(annotationId);
      } else {
        setPageNumber(page);
        setFocusedAnnotationId(annotationId);
      }
      if (user?.id) {
        void markAnnotationAsRead(annotationId, user.id);
      }
    },
    [focusedIsRight, versionSplit, rightView, setFocusedAnnotationId, setRightFocusedAnnotationId, user?.id]
  );

  const handlePlaceTakeoffPin = useCallback(
    async (pn: number, x: number, y: number) => {
      if (!pinMode || !dealId) return;
      try {
        await createTakeoffPin({
          documentId,
          documentUrl: url,
          leadId,
          dealId,
          structureId: pinMode.structureId,
          structureName: pinMode.structureName,
          pageNumber: pn,
          x,
          y,
          organizational: pinMode.organizational === true,
          createdAt: new Date(),
        });
        setPinMode(null);
        await loadTakeoffPins();
      } catch (err) {
        console.error('Error placing take-off pin:', err);
      }
    },
    [pinMode, dealId, documentId, url, leadId, loadTakeoffPins],
  );

  const handleDeleteTakeoffPin = useCallback(
    async (pin: PdfTakeoffPin) => {
      if (!pin.id || pin.id.startsWith('virtual-')) return;
      try {
        await deleteTakeoffPin(pin.id);
        await loadTakeoffPins();
      } catch (err) {
        console.error('Error deleting structure label:', err);
      }
    },
    [loadTakeoffPins],
  );

  const handleTakeoffPinClick = useCallback((pin: PdfTakeoffPin) => {
    setSelectedStructureFromPin(pin.structureId);
    setActiveTakeoffStructureId(pin.structureId);
    if (takeoffModeActive) {
      setMeasureSubMode('linear');
      return;
    }
    setStructureLabelsOpen(true);
  }, [takeoffModeActive]);

  const handleMoveTakeoffPin = useCallback(
    async (pin: PdfTakeoffPin, x: number, y: number) => {
      if (!dealId) return;
      const isVirtual = pin.id?.startsWith('virtual-');
      if (isVirtual) {
        try {
          const stacked = takeoffPins.find(
            p =>
              p.pageNumber === pin.pageNumber &&
              p.structureId === pin.structureId &&
              p.id &&
              !p.id.startsWith('virtual-') &&
              Math.hypot(p.x - x, p.y - y) < SAVED_PIN_DEDUPE_RADIUS,
          );
          if (stacked?.id) {
            await updateTakeoffPin(stacked.id, { x, y, pageNumber: pin.pageNumber });
          } else {
            await createTakeoffPin({
              documentId,
              documentUrl: url,
              leadId,
              dealId,
              structureId: pin.structureId,
              structureName: pin.structureName,
              pageNumber: pin.pageNumber,
              x,
              y,
              createdAt: new Date(),
            });
          }
          await loadTakeoffPins();
        } catch (err) {
          console.error('Error saving take-off pin position:', err);
        }
        return;
      }
      if (!pin.id) return;
      setTakeoffPins(prev => prev.map(p => (p.id === pin.id ? { ...p, x, y } : p)));
      try {
        await updateTakeoffPin(pin.id, { x, y });
      } catch (err) {
        console.error('Error moving take-off pin:', err);
        await loadTakeoffPins();
      }
    },
    [dealId, documentId, url, leadId, takeoffPins, loadTakeoffPins],
  );

  const exitTakeoffMode = useCallback(() => {
    setTakeoffModeActive(false);
    setViewerMode('comment');
    setSpaceHeld(false);
    setPinMode(null);
    setStructureLabelsOpen(false);
    setSelectedStructureFromPin(null);
    setActiveTakeoffStructureId(null);
    setLinkModalMeasurement(null);
    setEditingLink(null);
  }, []);

  const enterTakeoffMode = useCallback(() => {
    if (!dealId) {
      setLockedToast('Link this project to a deal to use take-off.');
      return;
    }
    setTakeoffModeActive(true);
    setViewerMode('takeoff');
    setMeasureSubMode('linear');
    (document.activeElement as HTMLElement | null)?.blur();
    if (!calibration) {
      setLockedToast('Set a scale before take-off.');
      setCalibrationModalOpen(true);
    }
  }, [dealId, calibration]);

  const lockTakeoffEditScroll = useCallback(() => {
    const wrap = canvasWrapRef.current;
    const snap = spaceScrollLockRef.current ?? capturePdfScrollSnap(wrap);
    if (!snap) return;
    pendingScrollRestoreRef.current = snap;
    spaceScrollLockRef.current = snap;
    restorePdfScrollSnap(wrap, snap);
  }, []);

  const openLinkModalForMeasurement = useCallback((measurement: PdfMeasurement) => {
    if (!dealId || !measurement.id) return;
    lockTakeoffEditScroll();
    setSpaceHeld(false);
    const existing = linkByMeasurementId.get(measurement.id);
    setEditingLink(existing ?? null);
    setLinkModalMeasurement(measurement);
  }, [dealId, linkByMeasurementId, lockTakeoffEditScroll]);

  const handleSaveTakeoffLink = useCallback(
    async (data: TakeoffLinkFormData, action: 'add' | 'add_next') => {
      if (!user?.id || !dealId || !linkModalMeasurement?.id) {
        throw new Error('Missing user, deal, or measurement — cannot save take-off item.');
      }
      const geometricFt = computeMeasurementLengthFt(
        linkModalMeasurement,
        calibration,
        pageNaturalSize.w,
        pageNaturalSize.h,
      );
      const measuredLength =
        geometricFt != null && !Number.isNaN(geometricFt)
          ? roundTimberLengthFt(geometricFt)
          : editingLink?.measuredLength;
      const payload = {
        documentId,
        documentUrl: url,
        dealId,
        leadId,
        measurementId: linkModalMeasurement.id,
        pageNumber: linkModalMeasurement.pageNumber,
        structureId: data.structureId,
        structureName: data.structureName,
        name: data.name,
        species: data.species,
        grade: data.grade,
        dimensions: standardizeTimberDimensions(data.dimensions),
        quantity: data.quantity,
        moistureContent: data.moistureContent,
        measuredLength: measuredLength != null && !Number.isNaN(measuredLength) ? measuredLength : undefined,
        measuredLengthUnit: 'ft' as const,
        unitCost: data.unitCost,
        notes: data.notes || undefined,
        userId: user.id,
        userName: user.name || user.email || 'Unknown',
      };
      try {
        if (editingLink?.id) {
          await updateTakeoffLink(editingLink.id, payload);
        } else {
          await createTakeoffLink(payload);
        }
        setActiveTakeoffStructureId(data.structureId);
        setLinkModalMeasurement(null);
        setEditingLink(null);
        if (action === 'add_next') {
          setMeasureSubMode('linear');
        }
      } catch (err) {
        console.error('Error saving takeoff link:', err);
        throw err;
      }
    },
    [user, dealId, linkModalMeasurement, documentId, url, leadId, editingLink, calibration, pageNaturalSize.w, pageNaturalSize.h],
  );

  const handleSkipTakeoffLink = useCallback(() => {
    setLinkModalMeasurement(null);
    setEditingLink(null);
  }, []);

  const handleCancelTakeoffLink = useCallback(async (deleteMeasurementOnCancel: boolean) => {
    if (deleteMeasurementOnCancel && linkModalMeasurement?.id && !editingLink) {
      try {
        await deleteMeasurement(linkModalMeasurement.id);
      } catch (err) {
        console.error('Error deleting measurement:', err);
      }
    }
    setLinkModalMeasurement(null);
    setEditingLink(null);
  }, [linkModalMeasurement, editingLink]);

  const handleJumpToMeasurement = useCallback((targetPage: number, _measurementId: string) => {
    setPageNumber(targetPage);
  }, []);

  const onDocumentLoadSuccess = useCallback(
    async (doc: { numPages: number; getPage: (n: number) => Promise<unknown> }) => {
      pdfDocForScaleRef.current = doc as PdfJsLikeDoc;
      setNumPages(doc.numPages);
      setPageNumber(initialPageNumber ?? 1);
      setLoading(false);
      setError(null);
      try {
        const page = (await doc.getPage(1)) as { getViewport: (o: { scale: number }) => { width: number; height: number } };
        const vp = page.getViewport({ scale: 1 });
        setPageWidth(vp.width);
        setPageNaturalSize({ w: vp.width, h: vp.height });
      } catch {
        setPageWidth(612);
        setPageNaturalSize({ w: 612, h: 792 });
      }
    },
    [initialPageNumber]
  );

  useEffect(() => {
    if (calibration || scaleDetectAttemptedRef.current || !pdfDocForScaleRef.current) return;
    scaleDetectAttemptedRef.current = true;
    (async () => {
      const detected = await detectPdfScaleFromDocument(pdfDocForScaleRef.current!, 2);
      if (!detected) {
        setScaleDetectionBanner('No drawing scale found — measurements unavailable until scale is set.');
        return;
      }
      if (!user?.id) return;
      await saveCalibration({
        documentId,
        documentUrl: url,
        pageNumber,
        mode: 'standard',
        standardScaleId: detected.standardScaleId,
        realPerPdfPoint: detected.realPerPdfPoint,
        unitSystem: detected.unitSystem,
        baseUnit: detected.baseUnit,
        displayUnit: detected.displayUnit,
        pageNaturalWidth: pageNaturalSize.w,
        pageNaturalHeight: pageNaturalSize.h,
        leadId,
        projectId,
        userId: user.id,
        userName: user.name || user.email || 'Staff',
      });
      setScaleDetectionBanner(null);
    })().catch(() => {
      setScaleDetectionBanner('No drawing scale found — measurements unavailable until scale is set.');
    });
  }, [calibration, documentId, url, pageNumber, pageNaturalSize, leadId, projectId, user]);

  /**
   * Compute the scale that makes the natural page fit *entirely* inside the
   * canvas wrap (both axes), with a small padding so the page isn't right up
   * against the edges. Returns null when measurements aren't ready yet.
   */
  const computeFitScale = useCallback((): number | null => {
    const wrap = canvasWrapRef.current;
    if (!wrap || !pageWidth) return null;
    const pageH = pageNaturalSize.h || pageWidth * 1.294;
    const availW = wrap.clientWidth - VIEWER_FIT_PADDING_PX;
    const availH = wrap.clientHeight - VIEWER_FIT_PADDING_PX;
    return computeViewerFitScale(availW, availH, pageWidth, pageH);
  }, [pageWidth, pageNaturalSize.h]);

  useEffect(() => {
    if (pageWidth && canvasWrapRef.current && fitMode) {
      const fit = computeFitScale();
      if (fit != null) setScale(fit);
    }
  }, [pageWidth, pageNaturalSize.h, fitMode, computeFitScale]);

  useEffect(() => {
    initialNavAppliedRef.current = null;
  }, [documentId, url]);

  useEffect(() => {
    if (!initialAnnotationId && initialPageNumber == null) return;
    const navKey = `${documentId}|${initialAnnotationId ?? ''}|${initialPageNumber ?? ''}`;
    if (initialNavAppliedRef.current === navKey) return;

    if (initialPageNumber != null) {
      initialNavAppliedRef.current = navKey;
      setPageNumber(initialPageNumber);
      if (initialAnnotationId) {
        setFocusedAnnotationId(initialAnnotationId);
      }
      return;
    }

    if (!initialAnnotationId || loadingAnnotations) return;
    const target = annotations.find((a) => a.id === initialAnnotationId);
    if (!target?.pageNumber) return;

    initialNavAppliedRef.current = navKey;
    setPageNumber(target.pageNumber);
    setFocusedAnnotationId(initialAnnotationId);
  }, [
    documentId,
    initialAnnotationId,
    initialPageNumber,
    loadingAnnotations,
    annotations,
    setFocusedAnnotationId,
  ]);

  const effectiveScale = fitMode ? (computeFitScale() ?? scale) : scale;

  const displayScale = fitMode ? effectiveScale : scale;

  const targetRenderScale = useMemo(
    () => capPdfRenderScale(displayScale, pageNaturalSize.w, pageNaturalSize.h),
    [displayScale, pageNaturalSize.w, pageNaturalSize.h]
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (Math.abs(targetRenderScale - baseScaleRef.current) < 0.001) return;
    if (!initialScaleSetRef.current) {
      initialScaleSetRef.current = true;
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
      return;
    }
    debounceRef.current = setTimeout(() => {
      setBaseScale(targetRenderScale);
      baseScaleRef.current = targetRenderScale;
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [targetRenderScale]);

  const zoomRatio = displayScale / baseScale;
  const searchActive = searchOpen && searchQuery.trim().length > 0;
  const renderTextLayer =
    shouldRenderPdfTextLayer(zoomRatio) && (viewerMode === 'comment' || searchOpen);
  const visualWidth = pageNaturalSize.w * displayScale;
  const visualHeight = pageNaturalSize.h * displayScale;
  const rightRenderTextLayer =
    shouldRenderPdfTextLayer(rightView.zoomRatio) && (viewerMode === 'comment' || searchOpen);

  const focusedPageNumber = focusedIsRight ? rightView.pageNumber : pageNumber;
  const focusedNumPages = focusedIsRight ? rightView.numPages : numPages;
  const focusedDisplayScale = focusedIsRight ? rightView.displayScale : displayScale;
  const focusedFitMode = focusedIsRight ? rightView.fitMode : fitMode;
  const focusedFile = focusedIsRight && versionSplit && rightSibling ? rightSibling.url : fileUrl;
  const focusedTitle = focusedIsRight && versionSplit && rightSibling
    ? (rightSibling.versionLabel || rightSibling.title)
    : title;
  const focusedAnnotations = focusedIsRight && versionSplit ? rightAnnotations : annotations;
  const rightPageHits = searchHits.filter(h => h.pageNumber === rightView.pageNumber);
  const leftPageHits = searchHits.filter(h => h.pageNumber === pageNumber);
  const activeHit = searchHits[searchIndex] || null;
  const leftActiveHitIndex = activeHit && !focusedIsRight
    ? leftPageHits.findIndex(h => h.itemIndex === activeHit.itemIndex && h.pageNumber === activeHit.pageNumber)
    : -1;
  const rightActiveHitIndex = activeHit && focusedIsRight
    ? rightPageHits.findIndex(h => h.itemIndex === activeHit.itemIndex && h.pageNumber === activeHit.pageNumber)
    : -1;

  useEffect(() => {
    if (!fitMode || !pageWidth || !canvasWrapRef.current) return;
    const wrap = canvasWrapRef.current;
    const update = () => {
      const fit = computeFitScale();
      if (fit != null) setScale(fit);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [fitMode, pageWidth, pageNaturalSize.h, computeFitScale]);

  const markupInteractive =
    (viewerMode === 'draw' || (viewerMode === 'engineer' && canEngineerMarkup)) &&
    !pinMode &&
    !takeoffModeActive;
  const annotationInteractive = viewerMode === 'comment' && !pinMode && !takeoffModeActive;
  const measureInteractive = (viewerMode === 'measure' || viewerMode === 'takeoff') && !pinMode && twoPointCapture === 'inactive';
  /** Draw/measure/calibration live inside `.pdf-page-transform`; raise above the comment overlay when active. */
  const pageToolsElevated =
    markupInteractive || measureInteractive || twoPointCapture !== 'inactive';
  const pageTransformZIndex = pageToolsElevated ? 10 : 1;
  const annotationOverlayZIndex = annotationInteractive ? 10 : 2;

  const publishDocCursor = !!user?.id && !isInvitedTrade;
  const docCursorColor = useMemo(() => (user?.id ? cursorColorForUid(user.id) : ''), [user?.id]);
  const lastDocCursorSentRef = useRef(0);

  const handleDocPresenceMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!publishDocCursor || !user?.id) return;
      const now = Date.now();
      if (now - lastDocCursorSentRef.current < 150) return;
      lastDocCursorSentRef.current = now;
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const { x, y } = pointerToPageNorm(e.clientX, e.clientY, rect);
      void publishDocumentCursor(documentId, {
        uid: user.id,
        name: user.name || user.email || 'Someone',
        color: docCursorColor,
        x,
        y,
        pageNumber,
      });
    },
    [publishDocCursor, user?.id, user?.name, user?.email, docCursorColor, documentId, pageNumber],
  );

  const handleDocPresenceLeave = useCallback(() => {
    if (!publishDocCursor || !user?.id) return;
    void clearDocumentCursor(documentId, user.id);
  }, [publishDocCursor, user?.id, documentId]);

  useEffect(() => {
    if (!publishDocCursor || !user?.id) return;
    return () => {
      void clearDocumentCursor(documentId, user.id);
    };
  }, [publishDocCursor, user?.id, documentId]);

  /* ---------------- Zoom-to-cursor ---------------- */
  // We capture where the cursor sits in *page coordinates* before changing the
  // scale. After React commits the new container size, the layout effect below
  // adjusts scrollLeft/scrollTop so that same page point lands back under the
  // cursor — i.e. zoom anchors to the cursor instead of the top-left corner.
  const pendingZoomAnchor = useRef<null | {
    /** Cursor position normalized within the current page-container (0–1, can go negative if outside). */
    nx: number;
    ny: number;
    /** Cursor position in canvas-wrap-local pixels. */
    cursorX: number;
    cursorY: number;
  }>(null);

  const captureZoomAnchor = useCallback((clientX?: number, clientY?: number) => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;
    const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
    const rect = wrap.getBoundingClientRect();
    let cursorX = rect.width / 2;
    let cursorY = rect.height / 2;
    if (
      clientX !== undefined &&
      clientY !== undefined &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      cursorX = clientX - rect.left;
      cursorY = clientY - rect.top;
    }
    if (!pageContainer) {
      pendingZoomAnchor.current = { nx: 0.5, ny: 0.5, cursorX, cursorY };
      return;
    }
    const scrollX = wrap.scrollLeft + cursorX;
    const scrollY = wrap.scrollTop + cursorY;
    const pcW = Math.max(1, pageContainer.offsetWidth);
    const pcH = Math.max(1, pageContainer.offsetHeight);
    const nx = (scrollX - pageContainer.offsetLeft) / pcW;
    const ny = (scrollY - pageContainer.offsetTop) / pcH;
    pendingZoomAnchor.current = { nx, ny, cursorX, cursorY };
  }, []);

  useLayoutEffect(() => {
    const wrap = canvasWrapRef.current;
    const anchor = pendingZoomAnchor.current;
    if (!wrap || !anchor) return;
    pendingZoomAnchor.current = null;
    const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
    if (!pageContainer) return;
    const pcW = pageContainer.offsetWidth;
    const pcH = pageContainer.offsetHeight;
    const newContentX = pageContainer.offsetLeft + anchor.nx * pcW;
    const newContentY = pageContainer.offsetTop + anchor.ny * pcH;
    wrap.scrollLeft = Math.max(0, newContentX - anchor.cursorX);
    wrap.scrollTop = Math.max(0, newContentY - anchor.cursorY);
  }, [scale, fitMode]);

  const zoomIn = () => {
    if (focusedIsRight) {
      rightView.zoomIn();
      return;
    }
    captureZoomAnchor();
    setFitMode(false);
    setScale(s => clampViewerZoom(s * VIEWER_ZOOM_STEP));
  };
  const zoomOut = () => {
    if (focusedIsRight) {
      rightView.zoomOut();
      return;
    }
    captureZoomAnchor();
    setFitMode(false);
    setScale(s => clampViewerZoom(s / VIEWER_ZOOM_STEP));
  };
  const zoomToFit = () => {
    if (focusedIsRight) {
      rightView.zoomToFit();
      return;
    }
    setFitMode(true);
  };

  const openSameSplit = useCallback(() => {
    if (!splitAllowed) return;
    setSplitKind('same');
    setSyncViews(false);
    setFocusedPane('left');
  }, [splitAllowed]);

  const openVersionSplit = useCallback((siblingId?: string) => {
    if (!splitAllowed) return;
    const target = siblingId
      ? pdfSiblings.find(v => v.documentId === siblingId)
      : previousSibling;
    if (!target) return;
    setRightSiblingId(target.documentId);
    setSplitKind('version');
    setSyncViews(true);
    setFocusedPane('left');
  }, [splitAllowed, pdfSiblings, previousSibling]);

  const closeSplit = useCallback(() => {
    setSplitKind(null);
    setFocusedPane('left');
    setRightSiblingId(null);
  }, []);

  const jumpSearchHit = useCallback((hit: PdfTextSearchHit, pane: 'left' | 'right') => {
    if (pane === 'right') {
      rightView.setPageNumber(hit.pageNumber);
      requestAnimationFrame(() => rightView.scrollToNorm(hit.x + hit.width / 2, hit.y + hit.height / 2));
      return;
    }
    setPageNumber(hit.pageNumber);
    requestAnimationFrame(() => {
      const wrap = canvasWrapRef.current;
      if (!wrap) return;
      const pageContainer = wrap.querySelector('.pdf-page-container') as HTMLElement | null;
      if (!pageContainer) return;
      wrap.scrollLeft = Math.max(0, pageContainer.offsetLeft + ((hit.x + hit.width / 2) / 100) * pageContainer.offsetWidth - wrap.clientWidth / 2);
      wrap.scrollTop = Math.max(0, pageContainer.offsetTop + ((hit.y + hit.height / 2) / 100) * pageContainer.offsetHeight - wrap.clientHeight / 2);
    });
  }, [rightView]);

  useEffect(() => {
    const pdf = focusedIsRight ? rightView.pdfDocRef.current : pdfDocForScaleRef.current;
    if (!searchOpen || !searchQuery.trim() || !pdf) {
      if (!searchQuery.trim()) {
        setSearchHits([]);
        setSearchIndex(0);
      }
      return;
    }
    let cancelled = false;
    void searchPdfDocument(pdf, searchQuery).then(hits => {
      if (cancelled) return;
      setSearchHits(hits);
      setSearchIndex(0);
      if (hits[0]) jumpSearchHit(hits[0], focusedPane);
    });
    return () => {
      cancelled = true;
    };
  }, [searchOpen, searchQuery, focusedIsRight, focusedNumPages, focusedFile, focusedPane, jumpSearchHit, rightView.pdfDocRef]);

  const goSearch = useCallback((dir: 1 | -1) => {
    if (searchHits.length === 0) return;
    const next = (searchIndex + dir + searchHits.length) % searchHits.length;
    setSearchIndex(next);
    jumpSearchHit(searchHits[next], focusedPane);
  }, [searchHits, searchIndex, focusedPane, jumpSearchHit]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!splitOpen || !syncViews || focusedPane !== 'left') return;
    rightView.applyRemoteView({ pageNumber, scale, fitMode });
  }, [splitOpen, syncViews, focusedPane, pageNumber, scale, fitMode, rightView.applyRemoteView]);

  useEffect(() => {
    if (!splitOpen || !syncViews || focusedPane !== 'right') return;
    setPageNumber(rightView.pageNumber);
    setScale(rightView.scale);
    setFitMode(rightView.fitMode);
  }, [splitOpen, syncViews, focusedPane, rightView.pageNumber, rightView.scale, rightView.fitMode]);

  useEffect(() => {
    if (!splitOpen || !syncViews) return;
    const left = canvasWrapRef.current;
    const right = rightView.canvasWrapRef.current;
    if (!left || !right) return;
    const onLeft = () => {
      if (syncingScrollRef.current) return;
      syncingScrollRef.current = true;
      right.scrollLeft = left.scrollLeft;
      right.scrollTop = left.scrollTop;
      syncingScrollRef.current = false;
    };
    const onRight = () => {
      if (syncingScrollRef.current) return;
      syncingScrollRef.current = true;
      left.scrollLeft = right.scrollLeft;
      left.scrollTop = right.scrollTop;
      syncingScrollRef.current = false;
    };
    left.addEventListener('scroll', onLeft, { passive: true });
    right.addEventListener('scroll', onRight, { passive: true });
    return () => {
      left.removeEventListener('scroll', onLeft);
      right.removeEventListener('scroll', onRight);
    };
  }, [splitOpen, syncViews, rightView.canvasWrapRef]);

  const handleStartPlanReview = useCallback(async () => {
    if (!user?.id || !projectId || planReviewBusy) return;
    setPlanReviewBusy(true);
    try {
      await startPlanReview({
        projectId,
        documentId,
        documentUrl: url,
        streamId,
        fileName: title,
        startedBy: user.id,
        startedByName: user.name || user.email || 'Someone',
      });
    } catch (e) {
      console.error('[PdfViewerModal] startPlanReview', e);
      window.alert(e instanceof Error ? e.message : 'Could not start plan review');
    } finally {
      setPlanReviewBusy(false);
    }
  }, [user, projectId, planReviewBusy, documentId, url, streamId, title]);

  const handleCancelPlanReview = useCallback(async () => {
    if (!user?.id || !activePlanReview?.id || planReviewBusy) return;
    if (!window.confirm('Cancel this plan review? Comments already made stay on the drawing but will not be sent as a summary.')) {
      return;
    }
    setPlanReviewBusy(true);
    try {
      await cancelPlanReview({
        reviewId: activePlanReview.id,
        cancelledBy: user.id,
        cancelledByName: user.name || user.email || 'Someone',
      });
    } catch (e) {
      console.error('[PdfViewerModal] cancelPlanReview', e);
      window.alert(e instanceof Error ? e.message : 'Could not cancel plan review');
    } finally {
      setPlanReviewBusy(false);
    }
  }, [user, activePlanReview, planReviewBusy]);

  const openCompletePlanReview = useCallback(async () => {
    if (!activePlanReview?.id || !user?.id) return;
    setPlanReviewBusy(true);
    try {
      const reviewAnns = await listAnnotationsForPlanReview(activePlanReview.id);
      const project = timberProjects.find((p) => p.id === projectId);
      const deal = project?.dealId
        ? enhancedDeals.find((d) => d.id === project.dealId)
        : dealId
          ? enhancedDeals.find((d) => d.id === dealId)
          : undefined;
      let uploadedBy: string | undefined;
      try {
        if (canUseAsFirestoreDocId(documentId)) {
          const { getDoc, doc } = await import('firebase/firestore');
          const { db } = await import('../../utils/firebase');
          const fileSnap = await getDoc(doc(db, 'projectFiles', documentId));
          if (fileSnap.exists()) {
            uploadedBy = fileSnap.data()?.uploadedBy as string | undefined;
          }
        }
      } catch {
        /* ignore invalid path or missing file */
      }
      const defaults = computePlanReviewDefaultRecipients({
        file: uploadedBy ? { uploadedBy } : null,
        project: project || null,
        deal: deal || null,
        crmUsers,
        reviewAnnotations: reviewAnns,
        excludeUserId: user.id,
      });
      setCompleteDefaults(defaults);
      setCompleteReviewAnnotations(reviewAnns);
      setCompleteModalOpen(true);
    } catch (e) {
      console.error('[PdfViewerModal] openCompletePlanReview', e);
      window.alert(e instanceof Error ? e.message : 'Could not prepare review summary');
    } finally {
      setPlanReviewBusy(false);
    }
  }, [
    activePlanReview,
    user,
    timberProjects,
    projectId,
    enhancedDeals,
    dealId,
    documentId,
    crmUsers,
  ]);

  const handleConfirmCompletePlanReview = useCallback(
    async (payload: { notifyUserIds: string[]; summaryNote: string; summaryNoteHtml: string }) => {
      if (!activePlanReview?.id || !user?.id) return;
      const reviewProjectId = (activePlanReview.projectId || projectId || '').trim();
      if (!reviewProjectId) return;
      const reviewAnns = await listAnnotationsForPlanReview(activePlanReview.id);
      const markupStrokeCount = await countMarkupStrokesForDocument(documentId);
      await completePlanReview({
        reviewId: activePlanReview.id,
        completedBy: user.id,
        completedByName: user.name || user.email || 'Someone',
        notifyUserIds: payload.notifyUserIds,
        summaryNote: payload.summaryNote,
        summaryNoteHtml: payload.summaryNoteHtml,
        annotationIds: reviewAnns.map((a) => a.id!).filter(Boolean),
        markupStrokeCount,
      });
      try {
        await postPlanReviewDesignGroupComment({
          reviewId: activePlanReview.id,
          projectId: reviewProjectId,
          authorId: user.id,
          authorName: user.name || user.email || 'Someone',
          fileName: title,
          commentCount: reviewAnns.length,
          taskGroups,
          timberProjects,
          projectTasks,
          projectOnboardings,
        });
      } catch (e) {
        console.error('[PdfViewerModal] postPlanReviewDesignGroupComment', e);
      }
      setCompleteModalOpen(false);
    },
    [
      activePlanReview,
      user,
      documentId,
      projectId,
      title,
      taskGroups,
      timberProjects,
      projectTasks,
      projectOnboardings,
    ],
  );

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const target = e.target as Node | null;
      const overRight = !!(splitOpen && rightView.canvasWrapRef.current && target && rightView.canvasWrapRef.current.contains(target));
      e.preventDefault();
      e.stopPropagation();
      const dy = Math.max(-80, Math.min(80, e.deltaY));
      const factor = Math.exp(-dy * ZOOM_WHEEL_SENSITIVITY);
      if (overRight) {
        rightView.captureZoomAnchor(e.clientX, e.clientY);
        rightView.setFitMode(false);
        rightView.setScale(s => clampViewerZoom(s * factor));
        return;
      }
      captureZoomAnchor(e.clientX, e.clientY);
      setFitMode(false);
      setScale(s => clampViewerZoom(s * factor));
    };
    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true });
  }, [captureZoomAnchor, splitOpen, rightView]);

  const captureInteractive = twoPointCapture !== 'inactive';

  const onDocumentLoadError = useCallback((err: Error) => {
    setError(err.message || 'Failed to load PDF');
    setLoading(false);
  }, []);

  const goPrev = useCallback(() => {
    if (focusedIsRight) {
      rightView.goPrev();
      return;
    }
    setPageNumber(p => Math.max(1, p - 1));
  }, [focusedIsRight, rightView.goPrev]);
  const goNext = useCallback(() => {
    if (focusedIsRight) {
      rightView.goNext();
      return;
    }
    setPageNumber(p => Math.min(numPages, p + 1));
  }, [focusedIsRight, numPages, rightView.goNext]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const step = pdfPageStepFromArrowKey(event);
      if (!step) return;
      event.preventDefault();
      if (step < 0) goPrev();
      else goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      // A comment list, calibration, or other dialog inside the viewer handles Escape first.
      if (
        commentsOpen
        || calibrationModalOpen
        || addressModalOpen
        || completeModalOpen
        || pinMode
        || linkModalMeasurement
      ) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [
    commentsOpen,
    calibrationModalOpen,
    addressModalOpen,
    completeModalOpen,
    pinMode,
    linkModalMeasurement,
    onClose,
  ]);

  // Per-page comment summary for the thumbnail rail. Unread = thread the
  // current viewer hasn't seen and didn't author themselves.
  const commentCountsByPage = useMemo(() => {
    const map = new Map<number, PageCommentSummary>();
    const uid = user?.id;
    for (const a of annotations) {
      const cur = map.get(a.pageNumber) || { total: 0, unread: 0 };
      cur.total += 1;
      const seen = uid && (a.readBy || []).includes(uid);
      if (uid && a.userId !== uid && !seen) cur.unread += 1;
      map.set(a.pageNumber, cur);
    }
    return map;
  }, [annotations, user?.id]);

  const calloutCountsByPage = useMemo(() => {
    const map = new Map<number, number>();
    for (const stroke of markupStrokes) {
      if (stroke.tool !== 'callout') continue;
      map.set(stroke.pageNumber, (map.get(stroke.pageNumber) || 0) + 1);
    }
    return map;
  }, [markupStrokes]);

  const linkModalMeasuredLengthFt = useMemo(() => {
    if (!linkModalMeasurement) return null;
    const ft = computeMeasurementLengthFt(
      linkModalMeasurement,
      calibration,
      pageNaturalSize.w,
      pageNaturalSize.h,
    );
    return ft != null ? roundTimberLengthFt(ft) : null;
  }, [linkModalMeasurement, calibration, pageNaturalSize.w, pageNaturalSize.h]);

  const linkModalNearestTakeoffLink = useMemo(() => {
    if (!linkModalMeasurement || editingLink) return null;
    return inferNearestTakeoffLinkForMeasurement(
      linkModalMeasurement,
      takeoffLinks,
      allMeasurements,
    );
  }, [linkModalMeasurement, editingLink, takeoffLinks, allMeasurements]);

  const linkModalInitialStructureId = useMemo(() => {
    if (!linkModalMeasurement || editingLink) {
      return selectedStructureFromPin || activeTakeoffStructureId;
    }
    return resolveInitialTakeoffStructureId(
      linkModalMeasurement,
      takeoffLinks,
      allMeasurements,
      {
        pinnedStructureId: selectedStructureFromPin,
        activeStructureId: activeTakeoffStructureId,
      },
    );
  }, [
    linkModalMeasurement,
    editingLink,
    takeoffLinks,
    allMeasurements,
    selectedStructureFromPin,
    activeTakeoffStructureId,
  ]);

  return (
    <div className="pdf-viewer-modal-overlay" onClick={onClose}>
      <div className="pdf-viewer-modal" onClick={e => e.stopPropagation()}>
        <header className="pdf-viewer-header">
          <button
            type="button"
            className={`pdf-viewer-icon-btn pdf-viewer-rail-toggle ${thumbRailVisible ? 'active' : ''}`}
            onClick={() => setThumbRailVisible(v => !v)}
            title={thumbRailVisible ? 'Hide page thumbnails' : 'Show page thumbnails'}
            aria-label="Toggle page thumbnails"
            aria-pressed={thumbRailVisible}
          >
            <PanelLeft size={16} />
          </button>
          <h2 className="pdf-viewer-title">{title}</h2>
          <div className="pdf-viewer-actions">
            {splitAllowed && (
              <button
                type="button"
                className={`pdf-viewer-toolbar-btn ${splitKind === 'same' ? 'active' : ''}`}
                onClick={() => (splitKind === 'same' ? closeSplit() : openSameSplit())}
                title={splitKind === 'same' ? 'Close split view' : 'Split this PDF'}
              >
                <Columns2 size={14} />
                Split
              </button>
            )}
            {splitAllowed && pdfSiblings.length > 0 && (
              <button
                type="button"
                className={`pdf-viewer-toolbar-btn ${splitKind === 'version' ? 'active' : ''}`}
                onClick={() => (splitKind === 'version' ? closeSplit() : openVersionSplit())}
                title={
                  splitKind === 'version'
                    ? 'Close compare and return to a single pane'
                    : 'Open the previous version beside this one so you can review two revisions of the same sheet'
                }
              >
                <GitCompare size={14} />
                Compare
              </button>
            )}
            {splitOpen && (
              <button
                type="button"
                className={`pdf-viewer-toolbar-btn ${syncViews ? 'active' : ''}`}
                onClick={() => setSyncViews(v => !v)}
                title={
                  syncViews
                    ? 'Sync is on — both panes share page, zoom, and scroll. S or click to look at different sheets independently'
                    : 'Keep both panes on the same page, zoom, and scroll position (S). Either pane can lead'
                }
              >
                <Link2 size={14} />
                Sync
              </button>
            )}
            {canManagePlanReview && projectId && !activePlanReview && (
              <button
                type="button"
                className="pdf-viewer-toolbar-btn"
                onClick={() => void handleStartPlanReview()}
                disabled={planReviewBusy}
                title="Start a quiet plan review — comments batch until you complete"
              >
                Start plan review
              </button>
            )}
            {canAccessTakeoff && dealId && (
              <button
                type="button"
                className={`pdf-viewer-toolbar-btn ${structureLabelsOpen ? 'active' : ''}`}
                onClick={() => {
                  setStructureLabelsOpen(open => {
                    if (open) setPinMode(null);
                    return !open;
                  });
                }}
                disabled={takeoffModeActive}
                title={
                  takeoffModeActive
                    ? 'Use the take-off session to place labels while take-off is active'
                    : 'Place structure name labels on any page'
                }
              >
                Labels
              </button>
            )}
            {canAccessTakeoff && (
              <button
                type="button"
                className={`pdf-viewer-toolbar-btn ${takeoffModeActive ? 'active' : ''}`}
                onClick={() => {
                  if (!takeoffModeActive) enterTakeoffMode();
                }}
                disabled={takeoffModeActive}
                aria-pressed={takeoffModeActive}
                title={takeoffModeActive ? 'Take-off mode active — use Exit take-off below' : 'Timber take-off'}
              >
                Take-off
              </button>
            )}
            {dealId && addressCtx && (
              <button
                type="button"
                className="pdf-viewer-icon-btn"
                onClick={() => setAddressModalOpen(true)}
                title={
                  addressCtx.location
                    ? `Project address: ${addressCtx.location} — click to edit`
                    : 'Set project address'
                }
                aria-label="Set project address"
              >
                <MapPin size={16} />
              </button>
            )}
            <button type="button" className="pdf-viewer-close-btn" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </header>

        {sharedAcrossStructures ? <SharedWholeProjectNote /> : null}

        {activePlanReview && (
          <div className="pdf-plan-review-banner" role="status">
            <span>
              Plan review in progress — comments are batched until you complete
              {activePlanReview.startedByName ? ` (started by ${activePlanReview.startedByName})` : ''}.
            </span>
            {canManagePlanReview && (
              <span className="pdf-plan-review-banner-actions">
                <button
                  type="button"
                  className="pdf-plan-review-banner-btn pdf-plan-review-banner-btn--primary"
                  disabled={planReviewBusy}
                  onClick={() => void openCompletePlanReview()}
                >
                  Complete review…
                </button>
                <button
                  type="button"
                  className="pdf-plan-review-banner-btn"
                  disabled={planReviewBusy}
                  onClick={() => void handleCancelPlanReview()}
                >
                  Cancel
                </button>
              </span>
            )}
          </div>
        )}

        <div className="pdf-viewer-body">
          {numPages > 0 && (
            <PageThumbnailRail
              file={focusedFile}
              numPages={focusedNumPages}
              currentPage={focusedPageNumber}
              onSelectPage={focusedIsRight ? rightView.setPageNumber : setPageNumber}
              commentCounts={commentCountsByPage}
              calloutCounts={calloutCountsByPage}
              visible={thumbRailVisible}
              onCollapse={() => setThumbRailVisible(false)}
              naturalAspect={
                focusedIsRight
                  ? rightView.pageNaturalSize.h / rightView.pageNaturalSize.w
                  : pageNaturalSize.h / pageNaturalSize.w
              }
            />
          )}
          <div className="pdf-viewer-main">
            {!takeoffModeActive ? (
              <ViewerModeBar
                mode={viewerMode}
                onModeChange={handleViewerModeChange}
                drawTool={drawTool}
                onDrawToolChange={setDrawTool}
                drawColor={drawColor}
                onDrawColorChange={setDrawColor}
                drawStrokeWidth={drawStrokeWidth}
                onDrawStrokeWidthChange={setDrawStrokeWidth}
                showEngineering={canEngineerMarkup}
                selectedStampId={selectedStampId}
                onStampChange={setSelectedStampId}
              />
            ) : (
              <div className="pdf-takeoff-mode-bar">
                <span className="pdf-takeoff-mode-label">
                  Take-off mode — draw measurements to link timber
                  {spaceHeld ? ' · Space held: all dims · click a label to edit' : ' · Hold Space: all dims · hover a label for detail'}
                </span>
                <button type="button" className="pdf-takeoff-mode-exit" onClick={exitTakeoffMode}>
                  Exit take-off
                </button>
              </div>
            )}
            {scaleDetectionBanner && !calibration && (
              <div className="pdf-scale-detection-banner" role="status">
                {scaleDetectionBanner}
              </div>
            )}
            {(viewerMode === 'measure' || viewerMode === 'takeoff') && (
              <MeasurementToolbar
                subMode={measureSubMode}
                onSubModeChange={setMeasureSubMode}
                calibration={calibration}
                pageNumber={pageNumber}
                onOpenCalibration={() => setCalibrationModalOpen(true)}
                onClearCalibration={onClearCalibration}
                onClearMeasurements={onClearPageMeasurements}
                displayUnitOverride={displayUnitOverride}
                onDisplayUnitOverrideChange={setDisplayUnitOverride}
                pitchMode={pitchMode}
              />
            )}
            {captureInteractive && (
              <div className="pdf-capture-banner">
                {twoPointCapture === 'awaitingFirst'
                  ? 'Click the first point of a known dimension on the page.'
                  : 'Now click the second point.'}
                <button
                  type="button"
                  className="pdf-capture-cancel"
                  onClick={() => {
                    setTwoPointCapture('inactive');
                    setTwoPointA(null);
                    setTwoPointB(null);
                    setCalibrationModalOpen(true);
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
            <div
              ref={splitStageRef}
              className={`pdf-viewer-split-stage${splitOpen ? ' is-split' : ''}`}
            >
            <div className="pdf-viewer-split-left" style={splitOpen ? { flex: `${splitRatio} 1 0` } : undefined}>
            <PdfViewerPane
              file={fileUrl}
              canvasWrapRef={canvasWrapRef}
              focused={!focusedIsRight}
              onFocus={() => setFocusedPane('left')}
              spaceHeld={spaceHeld}
              loading={loading}
              error={error}
              pageNumber={pageNumber}
              renderedPageNumber={renderedPageNumber}
              onPageRenderSuccess={() => setRenderedPageNumber(pageNumber)}
              visualWidth={visualWidth}
              visualHeight={visualHeight}
              zoomRatio={zoomRatio}
              baseScale={baseScale}
              renderTextLayer={renderTextLayer}
              pageTransformZIndex={pageTransformZIndex}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onDocumentLoadError={onDocumentLoadError}
              onPointerMove={handleDocPresenceMove}
              onPointerLeave={handleDocPresenceLeave}
              searchHits={searchActive && !focusedIsRight ? leftPageHits : undefined}
              activeSearchIndex={leftActiveHitIndex}
              paneLabel={
                splitOpen
                  ? (
                    <span className="pdf-viewer-pane-label-text">
                      {splitKind === 'version' ? openedVersionLabel : 'Left'}
                    </span>
                  )
                  : undefined
              }
              afterPage={
                <>
                  {!isInvitedTrade ? (
                    <DocumentPresenceLayer
                      documentId={documentId}
                      pageNumber={pageNumber}
                      selfUid={user?.id}
                    />
                  ) : null}
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: annotationOverlayZIndex,
                      pointerEvents: annotationInteractive && !focusedIsRight ? 'auto' : 'none',
                    }}
                  >
                    <AnnotationLayer
                      pageNumber={pageNumber}
                      scale={displayScale}
                      annotations={annotations.filter(a => a.pageNumber === pageNumber)}
                      interactive={annotationInteractive && !focusedIsRight}
                      loading={loadingAnnotations}
                      pinModeActive={!!pinMode}
                      onAddAnnotation={handleAddAnnotation}
                      onAddReply={handleAddReply}
                      onDeleteAnnotation={loadAnnotations}
                      onAnnotationMoved={loadAnnotations}
                      currentUserId={user?.id}
                      focusAnnotationId={focusedAnnotationId}
                      onFocusAnnotation={setFocusedAnnotationId}
                      onAnnotationViewed={loadAnnotations}
                      architectSurface={architectSurface}
                      pinAudience={effectivePinAudience}
                      onPinAudienceChange={setPinAudience}
                      architectName={pinArchitectName}
                      hasAssignedArchitect={hasAssignedArchitect}
                      notifiesWithoutMention={fileCommentNotifiesWithoutMention(leadId, documentId)}
                    />
                  </div>
                </>
              }
              wrapExtras={
                <>
                  {lockedToast && (
                    <div className="pdf-locked-toast">
                      {lockedToast}
                      <button
                        type="button"
                        className="pdf-locked-toast-action"
                        onClick={() => { setLockedToast(null); setCalibrationModalOpen(true); }}
                      >
                        Set scale
                      </button>
                    </div>
                  )}
                  {structureLabelsOpen && dealId && user?.id && !takeoffModeActive && (
                    <StructureLabelsPanel
                      pageNumber={pageNumber}
                      dealId={dealId}
                      leadId={leadId}
                      userId={user.id}
                      userName={user.name || user.email || 'Unknown'}
                      draftStructures={takeoffDraftStructures}
                      takeoffPins={takeoffPins}
                      pinMode={pinMode}
                      onRequestPin={(structureId, structureName) =>
                        setPinMode({ structureId, structureName, organizational: true })
                      }
                      onCancelPin={() => setPinMode(null)}
                      onClose={() => {
                        setStructureLabelsOpen(false);
                        setPinMode(null);
                      }}
                    />
                  )}
                  {takeoffModeActive && dealId && user?.id && (
                    <TakeoffSessionPanel
                      links={takeoffLinks}
                      measurements={allMeasurements}
                      draftStructures={takeoffDraftStructures}
                      dealId={dealId}
                      leadId={leadId}
                      documentId={documentId}
                      userId={user.id}
                      userName={user.name || user.email || 'Unknown'}
                      dealTitle={title}
                      contactName={leadName}
                      projectId={projectId}
                      onEditLink={(link, measurement) => openLinkModalForMeasurement(measurement)}
                      onJumpToMeasurement={handleJumpToMeasurement}
                      onLinksChanged={() => { /* subscription updates automatically */ }}
                      onRequestPin={(structureId, structureName) =>
                        setPinMode({ structureId, structureName, organizational: true })
                      }
                      pinModeActive={!!pinMode}
                    />
                  )}
                </>
              }
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: markupInteractive ? 10 : 2,
                  pointerEvents: markupInteractive && !focusedIsRight ? 'auto' : 'none',
                }}
              >
                <MarkupLayer
                  pageNumber={pageNumber}
                  scale={displayScale}
                  strokes={markupStrokes}
                  activeTool={drawTool}
                  color={drawColor}
                  baseStrokeWidth={drawStrokeWidth}
                  enabled={markupInteractive && !focusedIsRight}
                  currentUserId={user?.id}
                  onCommitStroke={onMarkupCommit}
                  onDeleteStroke={onMarkupDelete}
                  onUpdateStroke={onMarkupUpdate}
                  selectedStampId={selectedStampId}
                  canEraseAnyEngineering={canEngineerMarkup}
                />
              </div>
              {showStructureIndicators && (
                <div style={{ position: 'absolute', inset: 0, zIndex: 8, pointerEvents: 'none' }}>
                  <TakeoffPinLayer
                    pageNumber={pageNumber}
                    pins={structureIndicatorsOnPage}
                    takeoffLinks={takeoffLinks}
                    pinMode={pinMode}
                    onPlacePin={handlePlaceTakeoffPin}
                    onPinClick={handleTakeoffPinClick}
                    onMovePin={handleMoveTakeoffPin}
                    onCancelPinMode={() => setPinMode(null)}
                    onDeletePin={handleDeleteTakeoffPin}
                    allowPinDrag={!!dealId && !pinMode && (takeoffModeActive || structureLabelsOpen || currentPageHasTakeoffActivity)}
                    allowPinDelete={!!dealId && !pinMode && (takeoffModeActive || structureLabelsOpen)}
                  />
                </div>
              )}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: measureInteractive ? 10 : 4,
                  pointerEvents: measureInteractive && !focusedIsRight ? 'auto' : 'none',
                }}
              >
                <MeasurementLayer
                  pageNumber={pageNumber}
                  pageNaturalWidth={pageNaturalSize.w}
                  pageNaturalHeight={pageNaturalSize.h}
                  visualWidth={visualWidth}
                  visualHeight={visualHeight}
                  measurements={displayMeasurements}
                  calibration={calibration}
                  subMode={measureSubMode}
                  displayUnitOverride={displayUnitOverride || undefined}
                  pitchMode={pitchMode}
                  enabled={measureInteractive && !focusedIsRight}
                  onLockedAttempt={() => setLockedToast('Set a scale before measuring.')}
                  onCommit={onMeasurementCommit}
                  onUpdatePoints={onMeasurementUpdate}
                  onDelete={onMeasurementDelete}
                  selectionResetToken={selectionResetToken}
                  linkedMeasurementIds={linkedMeasurementIds}
                  takeoffLinksByMeasurementId={linkByMeasurementId}
                  takeoffMode={takeoffModeActive}
                  spaceHeld={spaceHeld}
                  onMeasurementSelect={openLinkModalForMeasurement}
                  onTakeoffEditPrepare={lockTakeoffEditScroll}
                />
              </div>
              {captureInteractive && (() => {
                const safeVw = Math.max(50, visualWidth);
                const safeVh = Math.max(50, visualHeight);
                const sx = 100 / safeVw;
                const sy = 100 / safeVh;
                return (
                  <div style={{ position: 'absolute', inset: 0, zIndex: 7 }}>
                    <svg
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                      style={{ width: '100%', height: '100%', cursor: 'crosshair', touchAction: 'none' }}
                      onClick={handleCaptureClick}
                    >
                      {twoPointA && (
                        <g transform={`translate(${twoPointA.x},${twoPointA.y}) scale(${sx},${sy})`}>
                          <circle r={9} fill="none" stroke="#3b82f6" strokeWidth={1.5} />
                          <circle r={4} fill="#3b82f6" stroke="#fff" strokeWidth={1.2} />
                        </g>
                      )}
                    </svg>
                  </div>
                );
              })()}
            </PdfViewerPane>
            </div>
            {splitOpen && (
              <>
                <div
                  className={`pdf-viewer-split-divider${splitDraggingRef.current ? ' dragging' : ''}`}
                  role="separator"
                  aria-orientation="vertical"
                  onPointerDown={e => {
                    e.preventDefault();
                    splitDraggingRef.current = true;
                    const stage = splitStageRef.current;
                    const startX = e.clientX;
                    const startRatio = splitRatio;
                    const width = stage?.clientWidth || 1;
                    const onMove = (ev: PointerEvent) => {
                      const next = Math.max(0.28, Math.min(0.72, startRatio + (ev.clientX - startX) / width));
                      setSplitRatio(next);
                    };
                    const onUp = () => {
                      splitDraggingRef.current = false;
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                  }}
                />
                <div className="pdf-viewer-split-right" style={{ flex: `${1 - splitRatio} 1 0` }}>
                  <PdfViewerPane
                    file={rightFileUrl || fileUrl}
                    canvasWrapRef={rightView.canvasWrapRef}
                    focused={focusedIsRight}
                    onFocus={() => setFocusedPane('right')}
                    loading={rightView.loading}
                    error={rightView.error}
                    pageNumber={rightView.pageNumber}
                    renderedPageNumber={rightView.renderedPageNumber}
                    onPageRenderSuccess={() => rightView.setRenderedPageNumber(rightView.pageNumber)}
                    visualWidth={rightView.visualWidth}
                    visualHeight={rightView.visualHeight}
                    zoomRatio={rightView.zoomRatio}
                    baseScale={rightView.baseScale}
                    renderTextLayer={rightRenderTextLayer}
                    pageTransformZIndex={pageTransformZIndex}
                    onDocumentLoadSuccess={rightView.onDocumentLoadSuccess}
                    onDocumentLoadError={rightView.onDocumentLoadError}
                    searchHits={searchActive && focusedIsRight ? rightPageHits : undefined}
                    activeSearchIndex={rightActiveHitIndex}
                    paneLabel={
                      splitKind === 'version' && pdfSiblings.length > 0 ? (
                        <label className="pdf-viewer-version-picker">
                          <span>Version</span>
                          <select
                            value={rightSibling?.documentId || ''}
                            onChange={e => {
                              setRightSiblingId(e.target.value);
                              setFocusedPane('right');
                            }}
                            onClick={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                          >
                            {pdfSiblings.map(v => (
                              <option key={v.documentId} value={v.documentId}>
                                {v.versionLabel || v.title}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : (
                        <span className="pdf-viewer-pane-label-text">Right</span>
                      )
                    }
                    afterPage={
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: annotationOverlayZIndex,
                          pointerEvents: annotationInteractive && focusedIsRight ? 'auto' : 'none',
                        }}
                      >
                        <AnnotationLayer
                          pageNumber={rightView.pageNumber}
                          scale={rightView.displayScale}
                          annotations={(versionSplit ? rightAnnotations : annotations).filter(a => a.pageNumber === rightView.pageNumber)}
                          interactive={annotationInteractive && focusedIsRight}
                          loading={versionSplit ? rightLoadingAnnotations : loadingAnnotations}
                          pinModeActive={false}
                          onAddAnnotation={versionSplit ? handleAddRightAnnotation : handleAddAnnotation}
                          onAddReply={versionSplit ? handleAddRightReply : handleAddReply}
                          onDeleteAnnotation={versionSplit ? loadRightAnnotations : loadAnnotations}
                          onAnnotationMoved={versionSplit ? loadRightAnnotations : loadAnnotations}
                          currentUserId={user?.id}
                          focusAnnotationId={versionSplit ? rightFocusedAnnotationId : focusedAnnotationId}
                          onFocusAnnotation={versionSplit ? setRightFocusedAnnotationId : setFocusedAnnotationId}
                          onAnnotationViewed={versionSplit ? loadRightAnnotations : loadAnnotations}
                          architectSurface={architectSurface}
                          pinAudience={effectivePinAudience}
                          onPinAudienceChange={setPinAudience}
                          architectName={pinArchitectName}
                          hasAssignedArchitect={hasAssignedArchitect}
                          notifiesWithoutMention={fileCommentNotifiesWithoutMention(
                            leadId,
                            versionSplit && rightSibling ? rightSibling.documentId : documentId,
                          )}
                        />
                      </div>
                    }
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: markupInteractive ? 10 : 2,
                        pointerEvents: markupInteractive && focusedIsRight ? 'auto' : 'none',
                      }}
                    >
                      <MarkupLayer
                        pageNumber={rightView.pageNumber}
                        scale={rightView.displayScale}
                        strokes={versionSplit ? rightMarkupStrokes : markupStrokes}
                        activeTool={drawTool}
                        color={drawColor}
                        baseStrokeWidth={drawStrokeWidth}
                        enabled={markupInteractive && focusedIsRight}
                        currentUserId={user?.id}
                        onCommitStroke={versionSplit ? onRightMarkupCommit : onMarkupCommit}
                        onDeleteStroke={onMarkupDelete}
                        onUpdateStroke={onMarkupUpdate}
                        selectedStampId={selectedStampId}
                        canEraseAnyEngineering={canEngineerMarkup}
                      />
                    </div>
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: measureInteractive ? 10 : 4,
                        pointerEvents: measureInteractive && focusedIsRight ? 'auto' : 'none',
                      }}
                    >
                      <MeasurementLayer
                        pageNumber={rightView.pageNumber}
                        pageNaturalWidth={rightView.pageNaturalSize.w}
                        pageNaturalHeight={rightView.pageNaturalSize.h}
                        visualWidth={rightView.visualWidth}
                        visualHeight={rightView.visualHeight}
                        measurements={rightPaneMeasurements}
                        calibration={rightPaneCalibration}
                        subMode={measureSubMode}
                        displayUnitOverride={displayUnitOverride || undefined}
                        pitchMode={pitchMode}
                        enabled={measureInteractive && focusedIsRight}
                        onLockedAttempt={() => setLockedToast('Set a scale before measuring.')}
                        onCommit={versionSplit ? onRightMeasurementCommit : onMeasurementCommit}
                        onUpdatePoints={onMeasurementUpdate}
                        onDelete={onMeasurementDelete}
                        selectionResetToken={selectionResetToken}
                        linkedMeasurementIds={versionSplit ? new Set() : linkedMeasurementIds}
                        takeoffLinksByMeasurementId={versionSplit ? new Map() : linkByMeasurementId}
                        takeoffMode={false}
                        spaceHeld={false}
                      />
                    </div>
                  </PdfViewerPane>
                </div>
              </>
            )}
            </div>

            <ViewerToolbar
              displayScale={focusedDisplayScale}
              fitMode={focusedFitMode}
              commentsOpen={commentsOpen}
              annotationCount={focusedAnnotations.length}
              onCommentsOpen={() => setCommentsOpen(true)}
              onZoomOut={zoomOut}
              onZoomIn={zoomIn}
              onZoomToFit={zoomToFit}
              minScale={VIEWER_ZOOM_MIN}
              maxScale={VIEWER_ZOOM_MAX}
              downloadUrl={focusedFile}
              downloadFilename={buildDownloadFilename(focusedTitle, '.pdf')}
              trailing={
                <>
                  {searchOpen ? (
                    <PdfSearchBar
                      query={searchQuery}
                      onQueryChange={setSearchQuery}
                      matchCount={searchHits.length}
                      activeIndex={searchIndex}
                      onPrev={() => goSearch(-1)}
                      onNext={() => goSearch(1)}
                      onClose={() => {
                        setSearchOpen(false);
                        setSearchQuery('');
                        setSearchHits([]);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className="pdf-viewer-toolbar-btn"
                      onClick={() => setSearchOpen(true)}
                      title="Find in document (⌘F)"
                      aria-label="Find in document"
                    >
                      <Search size={16} />
                    </button>
                  )}
                  <div className="pdf-viewer-pager">
                    <button type="button" onClick={goPrev} disabled={focusedPageNumber <= 1} aria-label="Previous page">
                      <ChevronLeft size={18} />
                    </button>
                    <span className="pdf-viewer-page-info">
                      {focusedPageNumber} / {focusedNumPages || '—'}
                    </span>
                    <button type="button" onClick={goNext} disabled={focusedPageNumber >= focusedNumPages} aria-label="Next page">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </>
              }
            />
          </div>
        </div>
      </div>

      {linkModalMeasurement && dealId && user?.id && (
        <TakeoffLinkModal
          measurement={linkModalMeasurement}
          measuredLengthFt={linkModalMeasuredLengthFt}
          dealId={dealId}
          leadId={leadId}
          draftStructures={takeoffDraftStructures}
          userId={user.id}
          userName={user.name || user.email || 'Unknown'}
          existingLink={editingLink}
          initialStructureId={linkModalInitialStructureId}
          nearestTakeoffLink={linkModalNearestTakeoffLink}
          sessionLinks={takeoffLinks}
          recommendedTimberDefaults={lastTimberDefaults}
          onRememberTimberDefaults={setLastTimberDefaults}
          onSave={handleSaveTakeoffLink}
          onSkip={handleSkipTakeoffLink}
          onCancel={handleCancelTakeoffLink}
          onClose={handleSkipTakeoffLink}
        />
      )}

      {commentsOpen && (
        <PdfCommentsPopover
          annotations={focusedAnnotations}
          currentPageNumber={focusedPageNumber}
          currentUserId={user?.id}
          onSelectComment={handleSelectComment}
          onClose={() => setCommentsOpen(false)}
        />
      )}

      {calibrationModalOpen && createPortal(
        <CalibrationModal
          pageNumber={pageNumber}
          pageNaturalWidth={pageNaturalSize.w}
          pageNaturalHeight={pageNaturalSize.h}
          numPages={numPages}
          existing={calibration}
          capturedPoints={twoPointA && twoPointB ? { a: twoPointA, b: twoPointB } : null}
          onRequestTwoPointCapture={handleRequestTwoPointCapture}
          onApply={onApplyCalibration}
          onClose={() => setCalibrationModalOpen(false)}
        />,
        document.body,
      )}

      {addressModalOpen && dealId && addressCtx && (
        <SetProjectAddressModal
          dealId={dealId}
          initialAddress={addressCtx.address}
          initialLocation={addressCtx.location}
          initialCoordinates={addressCtx.coordinates}
          onClose={() => setAddressModalOpen(false)}
          onSaved={({ address, location, coordinates, geocoded }) => {
            setAddressCtx(prev => ({
              projectId: prev?.projectId ?? null,
              dealId,
              leadId: prev?.leadId ?? null,
              address,
              location,
              coordinates,
            }));
            setAddressToast(geocoded ? 'Address saved & geocoded' : 'Address saved');
          }}
        />
      )}

      {addressToast && createPortal(
        <div className="pdf-address-toast">{addressToast}</div>,
        document.body,
      )}

      {completeModalOpen && activePlanReview?.id && (
        <PlanReviewCompleteModal
          reviewId={activePlanReview.id}
          fileName={title}
          reviewAnnotations={completeReviewAnnotations}
          defaultRecipientIds={completeDefaults}
          crmUsers={crmUsers}
          onCancel={() => setCompleteModalOpen(false)}
          onConfirm={handleConfirmCompletePlanReview}
        />
      )}
    </div>
  );
}

export default function PdfViewerModal(props: PdfViewerModalProps) {
  return (
    <PdfViewerErrorBoundary onClose={props.onClose}>
      <PdfViewerModalInner {...props} />
    </PdfViewerErrorBoundary>
  );
}
